import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Mail, Printer, Copy, Check, FileDown, Loader2, FileCode2, Sparkles } from "lucide-react";
import { toPng, toSvg } from "html-to-image";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite, resolveImageUrl } from "../context/SiteContext";
import QRCode from "../components/QRCode";
import SilverLogo from "../components/SilverLogo";
import PrinterBrief from "../components/PrinterBrief";
import {
  downloadAsSvg,
  emailSvgToPrinter,
  buildBusinessCardVectorPdf,
  buildFlyerVectorPdf,
  buildPostcardVectorPdf,
  nodeToSvgString,
} from "../utils/vectorExports";
import {
  THEMES,
  ThemedLetterhead,
  ThemedFlyer,
  ThemedSocialPost,
  ThemedPostcardFront,
  ThemedPostcardBack,
  ThemedWindowDecal,
} from "../components/marketing-themes";

const DOMAIN_URL = "https://letgowelllness.com";

const DOMAIN = "letgowelllness.com";

// People featured on cards
export const PEOPLE = [
  {
    key: "waleska",
    name: "Waleska Castillo",
    title: "Licensed Professional Counselor",
    creds: "LPC · LMT",
  },
  {
    key: "max",
    name: "Max Castillo",
    title: "Chief Financial Officer",
    creds: "CFO",
  },
];

// Download a DOM node as a high-resolution PNG.
// High-res download that renders ANY asset node at exact target pixel dimensions.
// This is the only path Instagram, Facebook, print, etc. should use — it guarantees
// the exported image is sized for the platform (no upscale blur, no IG re-compression).
//
// Usage:
//   downloadAtSize(node, "filename.png", 1080, 1080)   // Instagram square / profile / feed tile
//   downloadAtSize(node, "filename.png", 1080, 1920)   // Instagram story / portrait poster
//   downloadAtSize(node, "filename.png", 2100, 1200)   // Business card front (3.5x2 in @ 600 DPI)
async function downloadAtSize(node, filename, targetWidthPx, targetHeightPx, backgroundColor = "#243B2C") {
  if (!node) return;
  const sourceRect = node.getBoundingClientRect();
  const scale = targetWidthPx / sourceRect.width;
  const dataUrl = await toPng(node, {
    cacheBust: true,
    backgroundColor,
    width: targetWidthPx,
    height: targetHeightPx,
    canvasWidth: targetWidthPx,
    canvasHeight: targetHeightPx,
    pixelRatio: 1,
    style: {
      transform: `scale(${scale})`,
      transformOrigin: "top left",
      width: `${sourceRect.width}px`,
      height: `${sourceRect.height}px`,
    },
  });
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

async function downloadNode(node, filename, pixelRatio = 3) {
  if (!node) return;
  const dataUrl = await toPng(node, {
    pixelRatio,
    cacheBust: true,
    backgroundColor: "#FDFBF7",
  });
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  a.click();
}

// Backward-compat alias for the poster-specific helper.
export const downloadPosterAtSize = (node, filename, w = 1080, h = 1920) =>
  downloadAtSize(node, filename, w, h, "#243B2C");

// Render the node to a high-resolution PNG, then wrap it inside a print-ready
// PDF at the exact physical dimensions (inches). Print shops universally
// accept PDF — this saves users from format conversion at the print bureau.
// `widthIn` / `heightIn` are the FINAL paper dimensions of the artwork.
export async function downloadAsPDF(node, filename, widthIn, heightIn, backgroundColor = "#FDFBF7") {
  if (!node) return;
  // Render at 600 DPI for crisp print output.
  const DPI = 600;
  const targetWidthPx = Math.round(widthIn * DPI);
  const targetHeightPx = Math.round(heightIn * DPI);
  const sourceRect = node.getBoundingClientRect();
  const scale = targetWidthPx / sourceRect.width;
  const dataUrl = await toPng(node, {
    cacheBust: true,
    backgroundColor,
    width: targetWidthPx,
    height: targetHeightPx,
    canvasWidth: targetWidthPx,
    canvasHeight: targetHeightPx,
    pixelRatio: 1,
    style: {
      transform: `scale(${scale})`,
      transformOrigin: "top left",
      width: `${sourceRect.width}px`,
      height: `${sourceRect.height}px`,
    },
  });
  const pdf = new jsPDF({
    orientation: widthIn > heightIn ? "landscape" : "portrait",
    unit: "in",
    format: [widthIn, heightIn],
    compress: true,
  });
  pdf.addImage(dataUrl, "PNG", 0, 0, widthIn, heightIn, undefined, "FAST");
  pdf.save(filename);
}

// Quick mailto helper — opens the visitor's email client with a pre-filled
// message they can use to send a print order to their print shop. The artwork
// is downloaded first so they can attach the PDF from their Downloads folder.
export async function emailToPrinter(node, baseName, widthIn, heightIn, opts = {}) {  if (!node) return;
  const filename = `${baseName}.pdf`;
  await downloadAsPDF(node, filename, widthIn, heightIn, opts.backgroundColor || "#FDFBF7");
  const subject = encodeURIComponent(opts.subject || `Print order — ${baseName}`);
  const body = encodeURIComponent(
    `Hi,\n\nPlease find attached the print-ready artwork:\n\n` +
    `• File: ${filename}\n` +
    `• Size: ${widthIn}″ × ${heightIn}″\n` +
    `• Format: Print-ready PDF (600 DPI)\n` +
    `${opts.notes ? `• Notes: ${opts.notes}\n` : ""}` +
    `\nI've downloaded the file to my computer — please confirm you've received it once attached.\n\nThank you,\nLet Go Wellness`
  );
  // Open the user's default email client. Most browsers respect this on
  // desktop; on mobile it opens the mail app.
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
}

// Render any node to a PNG data URL at an exact pixel size — used by ZIP bundlers.
async function nodeToHdDataUrl(node, targetWidthPx, targetHeightPx, backgroundColor = "#243B2C") {
  if (!node) return null;
  const sourceRect = node.getBoundingClientRect();
  const scale = targetWidthPx / sourceRect.width;
  return toPng(node, {
    cacheBust: true,
    backgroundColor,
    width: targetWidthPx,
    height: targetHeightPx,
    canvasWidth: targetWidthPx,
    canvasHeight: targetHeightPx,
    pixelRatio: 1,
    style: {
      transform: `scale(${scale})`,
      transformOrigin: "top left",
      width: `${sourceRect.width}px`,
      height: `${sourceRect.height}px`,
    },
  });
}

function AssetSection({ id, eyebrow, title, sub, children, onDownload, onPrint, onEmail, onDownloadSvg, onVectorPdf, downloadLabel = "Download PDF" }) {
  return (
    <section id={id} data-testid={`asset-${id}`} className="border-t border-[#E2DCD0] pt-16 pb-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="overline text-[#9F5C45] mb-3">{eyebrow}</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] tracking-tight">
            {title}
          </h2>
          {sub && <p className="text-[#57534E] mt-3 max-w-xl text-sm md:text-base">{sub}</p>}
        </div>
        <div className="flex flex-wrap gap-3 print:hidden">
          {onDownload && (
            <button
              onClick={onDownload}
              data-testid={`download-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-primary text-xs tracking-[0.18em] uppercase"
            >
              <Download className="w-4 h-4" /> {downloadLabel}
            </button>
          )}
          {onVectorPdf && (
            <button
              onClick={onVectorPdf}
              data-testid={`vector-pdf-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs tracking-[0.18em] uppercase border-[#3A5A40] text-[#3A5A40]"
              title="Hand-coded vector PDF — text and shapes drawn as true PDF primitives (no rasterization)."
            >
              <Sparkles className="w-4 h-4" /> True Vector PDF
            </button>
          )}
          {onDownloadSvg && (
            <button
              onClick={onDownloadSvg}
              data-testid={`svg-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs tracking-[0.18em] uppercase"
              title="Scalable Vector Graphic — resolution-independent, ideal for any print scale."
            >
              <FileCode2 className="w-4 h-4" /> Download SVG
            </button>
          )}
          {onEmail && (
            <button
              onClick={onEmail}
              data-testid={`email-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs tracking-[0.18em] uppercase"
            >
              <Mail className="w-4 h-4" /> Email to printer
            </button>
          )}
          {onPrint && (
            <button
              onClick={onPrint}
              data-testid={`print-${id}`}
              className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs tracking-[0.18em] uppercase"
            >
              <Printer className="w-4 h-4" /> Print PDF
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-start gap-10 justify-center md:justify-start">
        {children}
      </div>
    </section>
  );
}

// — 3.5in × 2in standard US business card front —
export function CardFront({ person, brand, tagline, logoUrl, phone, email, addr1, addr2 }) {
  return (
    <div
      className="bg-[#FDFBF7] text-[#1C1917] relative overflow-hidden flex"
      style={{ width: "3.5in", height: "2in", boxShadow: "0 1px 0 #E2DCD0" }}
    >
      {/* Sage band with logo */}
      <div className="bg-[#3A5A40] flex flex-col items-center justify-center" style={{ width: "1.05in" }}>
        {logoUrl && (
          <SilverLogo src={logoUrl} alt={brand} size={{ w: "0.75in", h: "0.75in" }} />
        )}
        <p
          className="font-serif italic text-[#FDFBF7] mt-1 text-center leading-none"
          style={{ fontSize: "0.085in" }}
        >
          {tagline || "wellness"}
        </p>
      </div>
      {/* Content */}
      <div className="flex-1 flex flex-col justify-between px-4 py-3">
        <div>
          <p
            className="text-[#9F5C45] uppercase tracking-[0.22em] leading-none"
            style={{ fontSize: "0.07in" }}
          >
            {brand}
          </p>
          <h3
            className="font-serif font-light text-[#1C1917] leading-tight mt-1"
            style={{ fontSize: "0.2in" }}
          >
            {person.name}
          </h3>
          <p
            className="font-serif italic text-[#3A5A40] leading-tight"
            style={{ fontSize: "0.1in" }}
          >
            {person.title}
          </p>
          <p
            className="text-[#57534E] uppercase tracking-[0.18em] mt-0.5"
            style={{ fontSize: "0.065in" }}
          >
            {person.creds}
          </p>
        </div>

        <div className="border-t border-[#E2DCD0] pt-2 grid grid-cols-1 gap-0.5" style={{ fontSize: "0.075in" }}>
          <p className="text-[#1C1917]">{phone}</p>
          <p className="text-[#57534E]">{email}</p>
          <p className="text-[#57534E]">{addr1} · {addr2}</p>
        </div>
      </div>
    </div>
  );
}

// — Card back: QR + tagline + person credit —
export function CardBack({ person, brand }) {
  return (
    <div
      className="bg-[#3A5A40] text-[#FDFBF7] flex flex-col items-center justify-center relative overflow-hidden"
      style={{ width: "3.5in", height: "2in" }}
    >
      <div className="absolute top-3 left-4 right-4 flex justify-between items-center">
        <p
          className="font-serif italic text-[#FDFBF7]/80"
          style={{ fontSize: "0.085in" }}
        >
          a quiet space to let go.
        </p>
        <p className="uppercase tracking-[0.22em] text-[#FDFBF7]/70" style={{ fontSize: "0.06in" }}>
          {brand}
        </p>
      </div>

      <div className="bg-[#FDFBF7] p-1.5" style={{ marginTop: "0.1in" }}>
        <QRCode
          value={DOMAIN_URL}
          alt="QR — letgowelllness.com"
          style={{ width: "1.05in", height: "1.05in", objectFit: "contain", display: "block" }}
        />
      </div>

      <div className="absolute bottom-3 left-4 right-4 text-center">
        <p className="uppercase tracking-[0.28em]" style={{ fontSize: "0.072in" }}>
          {DOMAIN}
        </p>
        <p className="font-serif italic text-[#FDFBF7]/70 mt-0.5" style={{ fontSize: "0.07in" }}>
          {person.name} · {person.creds}
        </p>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// MIDNIGHT SERIES — Portrait orientation, dark navy + rose-gold logo.
// Inspired by editorial luxury cards — passed to client on request.
// ──────────────────────────────────────────────────────────────

const MIDNIGHT_NAVY = "#0E1B2E";
const ROSEGOLD_HAIRLINE = "rgba(229, 180, 153, 0.55)";

// Onyx series — deep black with gold-foil treatment.
const ONYX_BLACK = "#0A0908";
const GOLD_HAIRLINE = "rgba(242, 215, 122, 0.55)";
const GOLD_ACCENT = "#E8C669";

// Forest series — brand sage with gold-foil treatment.
const FOREST_SAGE = "#2C4C3B";
const FOREST_GOLD_HAIRLINE = "rgba(242, 215, 122, 0.5)";

// Imprint series — sage paper with a huge debossed monogram pressed into it.
const IMPRINT_SAGE = "#3A5A40";
const IMPRINT_DEEP = "#243B2C"; // darker sage used for "shadow" side of emboss
const IMPRINT_HIGHLIGHT = "rgba(255,255,255,0.18)";
const IMPRINT_SHADOW = "rgba(0,0,0,0.42)";

// — Midnight front · 2in × 3.5in portrait, big rose-gold logo —
export function MidnightCardFront({ brand, logoUrl }) {
  return (
    <div
      className="relative overflow-hidden flex flex-col items-center justify-center"
      style={{ width: "2in", height: "3.5in", background: MIDNIGHT_NAVY }}
    >
      {/* Outer hairline frame */}
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.13in", border: `1px solid ${ROSEGOLD_HAIRLINE}` }}
      />
      {logoUrl && (
        <SilverLogo
          src={logoUrl}
          alt={brand}
          variant="rosegold"
          size={{ w: "1.45in", h: "1.45in" }}
        />
      )}
      <div className="mt-3 flex flex-col items-center">
        <span
          className="block"
          style={{ width: "0.6in", height: "1px", background: ROSEGOLD_HAIRLINE }}
        />
        <p
          className="font-serif text-[#FDFBF7]/95 text-center tracking-tight mt-3"
          style={{ fontSize: "0.18in", lineHeight: 1 }}
        >
          {brand}
        </p>
        <p
          className="font-serif italic text-[#E5B499] text-center mt-1"
          style={{ fontSize: "0.1in" }}
        >
          a quiet space to let go.
        </p>
      </div>
    </div>
  );
}

// — Midnight back · 2in × 3.5in portrait, contact details —
export function MidnightCardBack({ person, brand, phone, email, addr1, addr2 }) {
  return (
    <div
      className="relative overflow-hidden flex flex-col"
      style={{ width: "2in", height: "3.5in", background: MIDNIGHT_NAVY, color: "#FDFBF7", padding: "0.25in 0.22in" }}
    >
      {/* Hairline frame */}
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.13in", border: `1px solid ${ROSEGOLD_HAIRLINE}` }}
      />

      <div className="relative z-10 flex-1 flex flex-col">
        <p
          className="uppercase tracking-[0.32em] text-[#E5B499] text-center"
          style={{ fontSize: "0.07in" }}
        >
          {brand}
        </p>

        <div style={{ marginTop: "0.32in" }}>
          <LuxNameBlock person={person} accent="#E5B499" />
        </div>

        <div
          className="mt-auto text-center"
          style={{ borderTop: `1px solid ${ROSEGOLD_HAIRLINE}`, paddingTop: "0.12in" }}
        >
          <p className="text-[#FDFBF7]/90" style={{ fontSize: "0.08in", lineHeight: 1.6 }}>
            {phone}
          </p>
          <p className="text-[#FDFBF7]/75 truncate" style={{ fontSize: "0.075in", lineHeight: 1.6 }}>
            {email}
          </p>
          <p className="text-[#FDFBF7]/75 mt-2" style={{ fontSize: "0.075in", lineHeight: 1.3 }}>
            {addr1}
          </p>
          <p className="text-[#FDFBF7]/75" style={{ fontSize: "0.075in", lineHeight: 1.3 }}>
            {addr2}
          </p>
          <p
            className="uppercase tracking-[0.24em] text-[#E5B499] mt-3"
            style={{ fontSize: "0.07in" }}
          >
            {DOMAIN}
          </p>
        </div>
      </div>
    </div>
  );
}

// Shared "lux" name treatment used by Midnight / Onyx / Forest cards.
// Goes for a wedding-invitation feel: small monogram glyph, large light serif name,
// hairline accents, italic title, wide-tracked credentials.
function LuxNameBlock({ person, accent = "#E5B499" }) {
  return (
    <div className="text-center">
      <span
        className="inline-block"
        style={{ width: "0.32in", height: "1px", background: accent, opacity: 0.65 }}
      />
      <p
        className="font-serif italic mt-2"
        style={{ fontSize: "0.11in", color: accent, lineHeight: 1 }}
      >
        ❦
      </p>
      <h3
        className="font-serif font-light text-[#FDFBF7] mt-1"
        style={{ fontSize: "0.24in", letterSpacing: "0.01em", lineHeight: 1.05 }}
      >
        {person.name}
      </h3>
      <span
        className="inline-block mt-3"
        style={{ width: "0.32in", height: "1px", background: accent, opacity: 0.65 }}
      />
      <p
        className="font-serif italic mt-2"
        style={{ fontSize: "0.1in", color: accent, lineHeight: 1.2 }}
      >
        {person.title}
      </p>
      <p
        className="uppercase tracking-[0.28em] text-[#FDFBF7]/65 mt-1"
        style={{ fontSize: "0.065in" }}
      >
        {person.creds}
      </p>
    </div>
  );
}

// — Onyx front · 2in × 3.5in portrait, gold foil logo on deep black —
export function OnyxCardFront({ brand, logoUrl }) {
  return (
    <div
      className="relative overflow-hidden flex flex-col items-center justify-center"
      style={{ width: "2in", height: "3.5in", background: ONYX_BLACK }}
    >
      {/* Outer hairline frame in gold */}
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.13in", border: `1px solid ${GOLD_HAIRLINE}` }}
      />
      {/* Subtle inner double-line accent (luxury detail) */}
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.17in", border: `1px solid ${GOLD_HAIRLINE}`, opacity: 0.3 }}
      />
      {logoUrl && (
        <SilverLogo
          src={logoUrl}
          alt={brand}
          variant="gold"
          size={{ w: "1.45in", h: "1.45in" }}
        />
      )}
      <div className="mt-3 flex flex-col items-center">
        <span
          className="block"
          style={{ width: "0.6in", height: "1px", background: GOLD_HAIRLINE }}
        />
        <p
          className="font-serif text-[#FDFBF7]/95 text-center tracking-tight mt-3"
          style={{ fontSize: "0.18in", lineHeight: 1 }}
        >
          {brand}
        </p>
        <p
          className="font-serif italic text-center mt-1"
          style={{ fontSize: "0.1in", color: GOLD_ACCENT }}
        >
          a quiet space to let go.
        </p>
      </div>
    </div>
  );
}

// — Onyx back · 2in × 3.5in portrait, contact details, gold accents —
export function OnyxCardBack({ person, brand, phone, email, addr1, addr2 }) {
  return (
    <div
      className="relative overflow-hidden flex flex-col"
      style={{ width: "2in", height: "3.5in", background: ONYX_BLACK, color: "#FDFBF7", padding: "0.25in 0.22in" }}
    >
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.13in", border: `1px solid ${GOLD_HAIRLINE}` }}
      />
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.17in", border: `1px solid ${GOLD_HAIRLINE}`, opacity: 0.3 }}
      />

      <div className="relative z-10 flex-1 flex flex-col">
        <p
          className="uppercase tracking-[0.32em] text-center"
          style={{ fontSize: "0.07in", color: GOLD_ACCENT }}
        >
          {brand}
        </p>

        <div style={{ marginTop: "0.32in" }}>
          <LuxNameBlock person={person} accent={GOLD_ACCENT} />
        </div>

        <div
          className="mt-auto text-center"
          style={{ borderTop: `1px solid ${GOLD_HAIRLINE}`, paddingTop: "0.12in" }}
        >
          <p className="text-[#FDFBF7]/90" style={{ fontSize: "0.08in", lineHeight: 1.6 }}>
            {phone}
          </p>
          <p className="text-[#FDFBF7]/75 truncate" style={{ fontSize: "0.075in", lineHeight: 1.6 }}>
            {email}
          </p>
          <p className="text-[#FDFBF7]/75 mt-2" style={{ fontSize: "0.075in", lineHeight: 1.3 }}>
            {addr1}
          </p>
          <p className="text-[#FDFBF7]/75" style={{ fontSize: "0.075in", lineHeight: 1.3 }}>
            {addr2}
          </p>
          <p
            className="uppercase tracking-[0.24em] mt-3"
            style={{ fontSize: "0.07in", color: GOLD_ACCENT }}
          >
            {DOMAIN}
          </p>
        </div>
      </div>
    </div>
  );
}

// — Forest front · 2in × 3.5in portrait, gold foil logo on sage —
export function ForestCardFront({ brand, logoUrl }) {
  return (
    <div
      className="relative overflow-hidden flex flex-col items-center justify-center"
      style={{ width: "2in", height: "3.5in", background: FOREST_SAGE }}
    >
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.13in", border: `1px solid ${FOREST_GOLD_HAIRLINE}` }}
      />
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.17in", border: `1px solid ${FOREST_GOLD_HAIRLINE}`, opacity: 0.3 }}
      />
      {logoUrl && (
        <SilverLogo
          src={logoUrl}
          alt={brand}
          variant="gold"
          size={{ w: "1.45in", h: "1.45in" }}
        />
      )}
      <div className="mt-3 flex flex-col items-center">
        <span
          className="block"
          style={{ width: "0.6in", height: "1px", background: FOREST_GOLD_HAIRLINE }}
        />
        <p
          className="font-serif text-[#FDFBF7]/95 text-center tracking-tight mt-3"
          style={{ fontSize: "0.18in", lineHeight: 1 }}
        >
          {brand}
        </p>
        <p
          className="font-serif italic text-center mt-1"
          style={{ fontSize: "0.1in", color: GOLD_ACCENT }}
        >
          a quiet space to let go.
        </p>
      </div>
    </div>
  );
}

// — Forest back · 2in × 3.5in portrait, contact details, gold on sage —
export function ForestCardBack({ person, brand, phone, email, addr1, addr2 }) {
  return (
    <div
      className="relative overflow-hidden flex flex-col"
      style={{ width: "2in", height: "3.5in", background: FOREST_SAGE, color: "#FDFBF7", padding: "0.25in 0.22in" }}
    >
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.13in", border: `1px solid ${FOREST_GOLD_HAIRLINE}` }}
      />
      <div
        className="absolute pointer-events-none"
        style={{ inset: "0.17in", border: `1px solid ${FOREST_GOLD_HAIRLINE}`, opacity: 0.3 }}
      />

      <div className="relative z-10 flex-1 flex flex-col">
        <p
          className="uppercase tracking-[0.32em] text-center"
          style={{ fontSize: "0.07in", color: GOLD_ACCENT }}
        >
          {brand}
        </p>

        <div style={{ marginTop: "0.32in" }}>
          <LuxNameBlock person={person} accent={GOLD_ACCENT} />
        </div>

        <div
          className="mt-auto text-center"
          style={{ borderTop: `1px solid ${FOREST_GOLD_HAIRLINE}`, paddingTop: "0.12in" }}
        >
          <p className="text-[#FDFBF7]/95" style={{ fontSize: "0.08in", lineHeight: 1.6 }}>
            {phone}
          </p>
          <p className="text-[#FDFBF7]/80 truncate" style={{ fontSize: "0.075in", lineHeight: 1.6 }}>
            {email}
          </p>
          <p className="text-[#FDFBF7]/80 mt-2" style={{ fontSize: "0.075in", lineHeight: 1.3 }}>
            {addr1}
          </p>
          <p className="text-[#FDFBF7]/80" style={{ fontSize: "0.075in", lineHeight: 1.3 }}>
            {addr2}
          </p>
          <p
            className="uppercase tracking-[0.24em] mt-3"
            style={{ fontSize: "0.07in", color: GOLD_ACCENT }}
          >
            {DOMAIN}
          </p>
        </div>
      </div>
    </div>
  );
}

// — Imprint front · 3.5in × 2in landscape, huge debossed brand mark on sage —
// Inspired by metal/letterpress cards: monogram pressed into the surface.
export function ImprintCardFront({ person, brand, logoUrl, scriptFont, textSize, logoSize }) {
  // Subtle brushed-paper gradient so the surface doesn't feel flat.
  const surface = `
    linear-gradient(135deg, ${IMPRINT_SAGE} 0%, ${IMPRINT_DEEP} 50%, ${IMPRINT_SAGE} 100%)
  `;
  const embossText = {
    color: IMPRINT_DEEP,
    textShadow: `
      -1.2px -1.2px 0 ${IMPRINT_SHADOW},
      1.2px  1.2px 0 ${IMPRINT_HIGHLIGHT}
    `,
    fontFamily: `'${scriptFont || "Pinyon Script"}', 'Cormorant Garamond', cursive`,
    fontWeight: 400,
    letterSpacing: "-0.005em",
    lineHeight: 0.85,
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{ width: "3.5in", height: "2in", background: surface }}
    >
      {/* Faint horizontal grain to suggest brushed surface */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />

      {/* Layout matches reference: BIG bold "LET GO" background + script "Wellness" overlaid on top */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {/* Background: HUGE bold serif "LET GO" — single line, debossed into the sage,
            spread wide but contained inside the card. We size to fit the inner width. */}
        <p
          className="font-serif uppercase"
          style={{
            ...embossText,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: 600,
            fontSize: textSize || "0.7in",
            letterSpacing: "0.16em",
            lineHeight: 1,
            margin: 0,
            padding: "0 0.2in",
            textAlign: "center",
            whiteSpace: "nowrap",
            width: "100%",
            boxSizing: "border-box",
          }}
          aria-hidden="true"
        >
          Let Go
        </p>

        {/* Foreground: "Wellness" in script gold foil — Sacramento signature style, smaller and longer */}
        <p
          style={{
            position: "absolute",
            fontFamily: "'Sacramento', cursive",
            fontSize: "0.34in",
            letterSpacing: "0.36em",
            lineHeight: 1,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            margin: 0,
            whiteSpace: "nowrap",
            background: "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
            filter: "drop-shadow(0 0.5px 0 rgba(0,0,0,0.4))",
          }}
          aria-hidden="true"
        >
          Wellness
        </p>
      </div>

      {/* Tiny brand name embossed at top-left */}
      <p
        className="absolute uppercase"
        style={{
          top: "0.16in",
          left: "0.2in",
          fontSize: "0.07in",
          letterSpacing: "0.34em",
          color: IMPRINT_DEEP,
          textShadow: `
            -0.5px -0.5px 0 ${IMPRINT_SHADOW},
            0.5px  0.5px 0 ${IMPRINT_HIGHLIGHT}
          `,
        }}
      >
        {brand}
      </p>

      {/* Vertical name on the right edge, like the reference */}
      <div
        className="absolute"
        style={{
          right: "0.16in",
          top: "50%",
          transform: "translateY(-50%) rotate(90deg)",
          transformOrigin: "right center",
          whiteSpace: "nowrap",
        }}
      >
        <p
          className="uppercase"
          style={{
            fontSize: "0.085in",
            letterSpacing: "0.32em",
            color: IMPRINT_DEEP,
            textShadow: `
              -0.5px -0.5px 0 ${IMPRINT_SHADOW},
              0.5px  0.5px 0 ${IMPRINT_HIGHLIGHT}
            `,
          }}
        >
          ⸻ {person.name} ⸻ {person.creds}
        </p>
      </div>

      {/* Small dot accent bottom-left for balance */}
      <span
        className="absolute"
        style={{
          left: "0.2in",
          bottom: "0.18in",
          width: "0.07in",
          height: "0.07in",
          borderRadius: "50%",
          background: IMPRINT_DEEP,
          boxShadow: `
            inset -0.5px -0.5px 0 ${IMPRINT_SHADOW},
            inset 0.5px 0.5px 0 ${IMPRINT_HIGHLIGHT}
          `,
        }}
      />
    </div>
  );
}

// — Imprint back · 3.5in × 2in landscape, two-column layout (logo+QR | contact details) —
export function ImprintCardBack({ person, brand, phone, phone2, email, addr1, addr2, logoUrl }) {
  const surface = `linear-gradient(135deg, ${IMPRINT_SAGE} 0%, ${IMPRINT_DEEP} 50%, ${IMPRINT_SAGE} 100%)`;
  return (
    <div
      className="relative overflow-hidden text-[#FDFBF7]"
      style={{ width: "3.5in", height: "2in", background: surface }}
    >
      {/* Brushed surface */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />

      <div
        className="relative z-10 grid h-full"
        style={{ gridTemplateColumns: "1.35in 1px 1fr", padding: "0.15in 0.18in" }}
      >
        {/* LEFT COLUMN — big gold-foil logo on top + QR with logo overlay */}
        <div className="flex flex-col items-center justify-between" style={{ padding: "0 0.04in" }}>
          {logoUrl ? (
            <SilverLogo
              src={logoUrl}
              alt={brand}
              variant="gold"
              size={{ w: "1.1in", h: "1.1in" }}
            />
          ) : (
            <div style={{ height: "1.1in" }} />
          )}
          <div className="bg-white p-0.5 relative" style={{ width: "0.78in", height: "0.78in" }}>
            <QRCode value={DOMAIN_URL} style={{ width: "100%", height: "100%", display: "block" }} />
            {/* Gold-foil logo overlay centered on the QR (QR uses error-correction H so this stays scannable) */}
            {logoUrl && (
              <div
                className="absolute bg-white flex items-center justify-center"
                style={{
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "0.22in",
                  height: "0.22in",
                  borderRadius: "0.04in",
                }}
              >
                <SilverLogo
                  src={logoUrl}
                  alt={brand}
                  variant="gold"
                  size={{ w: "0.18in", h: "0.18in" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Vertical divider */}
        <div style={{ background: "rgba(255,255,255,0.25)", width: "1px" }} />

        {/* RIGHT COLUMN — contact details */}
        <div className="flex flex-col" style={{ padding: "0 0 0 0.16in", gap: "0.04in" }}>
          <h3
            className="font-serif font-light text-[#FDFBF7]"
            style={{ fontSize: "0.16in", lineHeight: 1.1, letterSpacing: "0.005em" }}
          >
            {person.name}
          </h3>
          <p
            className="font-serif italic"
            style={{ fontSize: "0.085in", color: "#E8C669", lineHeight: 1.1 }}
          >
            {person.title}
          </p>
          <p
            className="uppercase tracking-[0.22em]"
            style={{ fontSize: "0.058in", color: "rgba(253,251,247,0.7)" }}
          >
            {person.creds}
          </p>

          <div
            className="mt-1 pt-1 flex flex-col"
            style={{
              borderTop: `1px solid rgba(255,255,255,0.2)`,
              fontSize: "0.075in",
              lineHeight: 1.4,
              gap: "0.02in",
            }}
          >
            <p className="text-[#FDFBF7]/95">
              <span className="text-[#FDFBF7]/55" style={{ fontSize: "0.055in", letterSpacing: "0.18em" }}>OFFICE </span>
              {phone}
            </p>
            {phone2 ? (
              <p className="text-[#FDFBF7]/85" style={{ fontSize: "0.07in" }}>
                <span className="text-[#FDFBF7]/55" style={{ fontSize: "0.055in", letterSpacing: "0.18em" }}>CELL </span>
                {phone2}
              </p>
            ) : null}
            <p className="text-[#FDFBF7]/85 truncate">{email}</p>
            <p className="text-[#FDFBF7]/85" style={{ fontSize: "0.07in", marginTop: "0.04in" }}>{addr1}</p>
            <p className="text-[#FDFBF7]/85" style={{ fontSize: "0.07in" }}>{addr2}</p>
            <p
              className="uppercase tracking-[0.24em] mt-auto"
              style={{ fontSize: "0.065in", color: "#E8C669" }}
            >
              {DOMAIN}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


// — Flyer 8.5in × 11in —
export function Flyer({ brand, tagline, logoUrl, phone, email, addr1, addr2, hoursWd, hoursWe, services }) {
  return (
    <div
      className="bg-[#FDFBF7] text-[#1C1917] relative overflow-hidden"
      style={{ width: "8.5in", height: "11in" }}
    >
      {/* Top sage band */}
      <div className="bg-[#3A5A40] text-[#FDFBF7] flex items-center justify-between px-12" style={{ height: "1.8in" }}>
        <div className="flex items-center gap-5">
          {logoUrl && (
            <SilverLogo src={logoUrl} alt={brand} size={{ w: "1.1in", h: "1.1in" }} />
          )}
          <div>
            <p className="uppercase tracking-[0.32em] text-[#FDFBF7]/80" style={{ fontSize: "0.12in" }}>
              {brand}
            </p>
            <p className="font-serif italic text-[#FDFBF7]" style={{ fontSize: "0.32in", lineHeight: 1 }}>
              {tagline || "a quiet space to let go."}
            </p>
          </div>
        </div>
        <p className="font-serif text-[#FDFBF7]/80" style={{ fontSize: "0.16in" }}>
          {DOMAIN}
        </p>
      </div>

      {/* Hero text */}
      <div className="px-12 pt-12">
        <p className="overline text-[#9F5C45]" style={{ fontSize: "0.11in", letterSpacing: "0.28em" }}>
          New Patients Welcome · In-person & Telehealth
        </p>
        <h1
          className="font-serif font-light text-[#1C1917] mt-6 leading-[0.95] tracking-tight"
          style={{ fontSize: "0.95in" }}
        >
          You don&apos;t have to <em className="italic text-[#3A5A40]">hold it</em> anymore.
        </h1>
        <p className="text-[#57534E] mt-6 max-w-[6in] leading-relaxed" style={{ fontSize: "0.17in" }}>
          A modern mental-health clinic in Waterbury, CT — built for the people who carry too much.
          Anxiety, depression, trauma, burnout, couples & family. We meet you where you are.
        </p>
      </div>

      {/* Services + QR row */}
      <div className="px-12 mt-12 grid grid-cols-3 gap-10">
        <div className="col-span-2">
          <p className="overline text-[#9F5C45] mb-5" style={{ fontSize: "0.09in", letterSpacing: "0.28em" }}>
            What we treat
          </p>
          <ul className="grid grid-cols-2 gap-y-3 gap-x-8">
            {(services || []).slice(0, 6).map((s) => (
              <li key={s.id || s.name} className="flex items-start gap-3 border-b border-[#E2DCD0] pb-3">
                <span className="w-2 h-2 bg-[#9F5C45] mt-2 flex-shrink-0" />
                <div>
                  <p className="font-serif text-[#1C1917]" style={{ fontSize: "0.2in", lineHeight: 1.1 }}>
                    {s.name}
                  </p>
                  <p className="text-[#57534E] mt-1" style={{ fontSize: "0.12in", lineHeight: 1.3 }}>
                    {s.blurb}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col items-center">
          <div className="bg-[#F4F1EA] p-4 border border-[#E2DCD0]">
            <QRCode
              value={DOMAIN_URL}
              style={{ width: "1.8in", height: "1.8in", objectFit: "contain", display: "block" }}
            />
          </div>
          <p className="uppercase tracking-[0.28em] text-[#1C1917] mt-4 text-center" style={{ fontSize: "0.1in" }}>
            Scan to enroll
          </p>
          <p className="font-serif italic text-[#3A5A40] text-center mt-1" style={{ fontSize: "0.16in" }}>
            {DOMAIN}
          </p>
        </div>
      </div>

      {/* Bottom band */}
      <div
        className="absolute bottom-0 left-0 right-0 bg-[#1C1917] text-[#FDFBF7] grid grid-cols-3 px-12 items-center"
        style={{ height: "1.5in" }}
      >
        <div>
          <p className="uppercase tracking-[0.28em] text-[#FDFBF7]/60" style={{ fontSize: "0.08in" }}>Visit</p>
          <p className="font-serif" style={{ fontSize: "0.17in", marginTop: "0.05in" }}>{addr1}</p>
          <p className="text-[#FDFBF7]/80" style={{ fontSize: "0.13in" }}>{addr2}</p>
        </div>
        <div>
          <p className="uppercase tracking-[0.28em] text-[#FDFBF7]/60" style={{ fontSize: "0.08in" }}>Call</p>
          <p className="font-serif" style={{ fontSize: "0.17in", marginTop: "0.05in" }}>{phone}</p>
          <p className="text-[#FDFBF7]/80" style={{ fontSize: "0.13in" }}>{email}</p>
        </div>
        <div className="text-right">
          <p className="uppercase tracking-[0.28em] text-[#FDFBF7]/60" style={{ fontSize: "0.08in" }}>Hours</p>
          <p className="font-serif" style={{ fontSize: "0.15in", marginTop: "0.05in" }}>{hoursWd}</p>
          <p className="text-[#FDFBF7]/80" style={{ fontSize: "0.13in" }}>{hoursWe}</p>
        </div>
      </div>
    </div>
  );
}

// — Social post 1080 × 1080 (square) —
export function SocialPost({ brand, tagline, logoUrl, phone }) {
  // We render at 5.4in × 5.4in CSS (518px) but export at high pixel ratio for 1080×1080+
  return (
    <div
      className="bg-[#FDFBF7] text-[#1C1917] relative overflow-hidden"
      style={{ width: "5.4in", height: "5.4in" }}
    >
      {/* Decorative band */}
      <div className="absolute top-0 left-0 right-0 bg-[#3A5A40]" style={{ height: "0.45in" }} />
      <div className="absolute bottom-0 left-0 right-0 bg-[#1C1917]" style={{ height: "0.95in" }} />

      <div className="absolute top-[0.45in] left-0 right-0 flex flex-col items-center justify-center px-6" style={{ height: "3.95in" }}>
        {logoUrl && (
          <img
            src={resolveImageUrl(logoUrl)}
            alt={brand}
            crossOrigin="anonymous"
            style={{ width: "0.9in", height: "0.9in", objectFit: "contain" }}
            className="mb-3"
          />
        )}
        <p className="uppercase tracking-[0.32em] text-[#9F5C45]" style={{ fontSize: "0.11in" }}>
          {brand}
        </p>
        <h2
          className="font-serif font-light text-[#1C1917] mt-4 text-center leading-[0.95] tracking-tight max-w-[4.5in]"
          style={{ fontSize: "0.6in" }}
        >
          You don&apos;t have to <em className="italic text-[#3A5A40]">hold it</em> anymore.
        </h2>
        <p className="font-serif italic text-[#3A5A40] mt-4 text-center" style={{ fontSize: "0.18in" }}>
          {tagline || "a quiet space to let go."}
        </p>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 text-[#FDFBF7] grid grid-cols-[auto_1fr] items-center px-6 gap-4" style={{ height: "0.95in" }}>
        <div className="bg-[#FDFBF7] p-1.5">
          <QRCode
            value={DOMAIN_URL}
            style={{ width: "0.75in", height: "0.75in", objectFit: "contain", display: "block" }}
          />
        </div>
        <div>
          <p className="font-serif" style={{ fontSize: "0.22in", lineHeight: 1 }}>{DOMAIN}</p>
          <p className="text-[#FDFBF7]/80 mt-1" style={{ fontSize: "0.13in" }}>
            {phone} · Waterbury, CT
          </p>
        </div>
      </div>
    </div>
  );
}

// — Referral Postcard 6in × 4in (landscape, USPS standard) —
export function PostcardFront({ brand, tagline, logoUrl }) {
  return (
    <div
      className="bg-[#3A5A40] text-[#FDFBF7] relative overflow-hidden"
      style={{ width: "6in", height: "4in" }}
    >
      <div className="absolute inset-0 flex items-center justify-center px-12">
        <div className="text-center">
          {logoUrl && (
            <SilverLogo
              src={logoUrl}
              alt={brand}
              size={{ w: "0.95in", h: "0.95in" }}
              className="mx-auto mb-4"
            />
          )}
          <p className="uppercase tracking-[0.32em] text-[#FDFBF7]/70" style={{ fontSize: "0.1in" }}>
            {brand}
          </p>
          <h2
            className="font-serif font-light text-[#FDFBF7] mt-6 leading-[0.95] tracking-tight"
            style={{ fontSize: "0.7in" }}
          >
            You came back for a <em className="italic text-[#E2DCD0]">reason</em>.
          </h2>
          <p className="font-serif italic text-[#FDFBF7]/85 mt-4" style={{ fontSize: "0.22in" }}>
            Share us with someone who needs it.
          </p>
        </div>
      </div>
      <p className="absolute bottom-3 right-5 font-serif italic text-[#FDFBF7]/60" style={{ fontSize: "0.11in" }}>
        {tagline || "a quiet space to let go."}
      </p>
    </div>
  );
}

export function PostcardBack({ brand, addr1, addr2, phone }) {
  return (
    <div
      className="bg-[#FDFBF7] text-[#1C1917] relative overflow-hidden grid"
      style={{ width: "6in", height: "4in", gridTemplateColumns: "1fr 1px 1fr" }}
    >
      {/* Left: Message side */}
      <div className="p-6 flex flex-col">
        <p className="overline text-[#9F5C45]" style={{ fontSize: "0.075in", letterSpacing: "0.28em" }}>
          From {brand}
        </p>
        <p className="font-serif italic text-[#3A5A40] mt-3 leading-snug" style={{ fontSize: "0.17in" }}>
          Thank you for trusting us with someone you love. Drop this card to anyone who could use
          a quiet, modern place to begin.
        </p>
        <div className="mt-auto pt-4 border-t border-[#E2DCD0]">
          <div className="flex items-center gap-3">
            <div className="bg-white p-1 border border-[#E2DCD0]">
              <QRCode
                value={DOMAIN_URL}
                style={{ width: "0.95in", height: "0.95in", objectFit: "contain", display: "block" }}
              />
            </div>
            <div>
              <p className="font-serif" style={{ fontSize: "0.16in" }}>{DOMAIN}</p>
              <p className="text-[#57534E]" style={{ fontSize: "0.1in" }}>{phone}</p>
              <p className="text-[#57534E]" style={{ fontSize: "0.1in" }}>{addr1}</p>
              <p className="text-[#57534E]" style={{ fontSize: "0.1in" }}>{addr2}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Center divider line */}
      <div className="bg-[#E2DCD0] w-px" />

      {/* Right: Address side */}
      <div className="p-6 flex flex-col">
        <div className="flex justify-end">
          <div className="border border-[#1C1917] flex items-center justify-center text-[#57534E]" style={{ width: "0.85in", height: "0.95in" }}>
            <span className="uppercase tracking-[0.22em] text-center leading-tight" style={{ fontSize: "0.065in" }}>
              Place<br />Stamp<br />Here
            </span>
          </div>
        </div>
        <div className="mt-auto">
          <div className="space-y-3">
            <div className="border-b border-[#1C1917]/30" style={{ height: "0.32in" }} />
            <div className="border-b border-[#1C1917]/30" style={{ height: "0.32in" }} />
            <div className="border-b border-[#1C1917]/30" style={{ height: "0.32in" }} />
            <div className="border-b border-[#1C1917]/30" style={{ height: "0.32in" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// — Window Decal · 6in × 6in square, sage on transparent-ish ground —
export function WindowDecal({ brand, logoUrl }) {
  return (
    <div
      className="bg-[#3A5A40] text-[#FDFBF7] relative overflow-hidden flex flex-col items-center justify-center"
      style={{ width: "6in", height: "6in", borderRadius: "0.5in" }}
    >
      {/* Decorative ring */}
      <div
        className="absolute pointer-events-none border border-[#FDFBF7]/15"
        style={{ inset: "0.35in", borderRadius: "0.35in" }}
      />
      {logoUrl && (
        <SilverLogo src={logoUrl} alt={brand} size={{ w: "1.6in", h: "1.6in" }} />
      )}
      <p className="font-serif text-[#FDFBF7] tracking-tight text-center" style={{ fontSize: "0.42in", marginTop: "0.12in", lineHeight: 1 }}>
        {brand}
      </p>
      <p className="font-serif italic text-[#FDFBF7]/80 mt-1 text-center" style={{ fontSize: "0.17in" }}>
        a quiet space to let go.
      </p>
      <div className="bg-[#FDFBF7] p-2 mt-4">
        <QRCode
          value={DOMAIN_URL}
          style={{ width: "1.3in", height: "1.3in", objectFit: "contain", display: "block" }}
        />
      </div>
      <p className="uppercase tracking-[0.32em] mt-3" style={{ fontSize: "0.09in" }}>
        Scan · {DOMAIN}
      </p>
    </div>
  );
}


// — Email signature: an HTML table snippet for max client compat —
export function EmailSignaturePreview({ person, brand, logoUrl, phone, email, addr1, addr2 }) {
  const logoAbs = logoUrl?.startsWith("http") ? logoUrl : `https://${DOMAIN}${logoUrl || ""}`;
  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      style={{ borderCollapse: "collapse", fontFamily: "Manrope, Arial, sans-serif", color: "#1C1917" }}
    >
      <tbody>
        <tr>
          <td style={{ verticalAlign: "top", paddingRight: 18, borderRight: "2px solid #3A5A40" }}>
            <img
              src={logoAbs}
              alt={brand}
              width="68"
              height="68"
              style={{ display: "block", objectFit: "contain" }}
            />
          </td>
          <td style={{ verticalAlign: "top", paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 20, color: "#1C1917" }}>
              {person.name}
            </div>
            <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontSize: 14, color: "#3A5A40", marginBottom: 6 }}>
              {person.title} · {person.creds}
            </div>
            <div style={{ color: "#9F5C45", letterSpacing: 2, textTransform: "uppercase", fontSize: 10, marginBottom: 6 }}>
              {brand}
            </div>
            <div style={{ color: "#57534E", fontSize: 12 }}>
              <a href={`tel:${phone.replace(/\s/g, "")}`} style={{ color: "#1C1917", textDecoration: "none" }}>{phone}</a>
              {" · "}
              <a href={`mailto:${email}`} style={{ color: "#1C1917", textDecoration: "none" }}>{email}</a>
            </div>
            <div style={{ color: "#57534E", fontSize: 12 }}>
              {addr1}, {addr2}
            </div>
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <a href={`https://${DOMAIN}`} style={{ color: "#3A5A40", textDecoration: "none", fontWeight: 600 }}>
                {DOMAIN}
              </a>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function buildSignatureHtml({ person, brand, logoUrl, phone, email, addr1, addr2 }) {
  const logoAbs = logoUrl?.startsWith("http") ? logoUrl : `https://${DOMAIN}${logoUrl || ""}`;
  return `<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-family:Manrope,Arial,sans-serif;color:#1C1917"><tbody><tr><td style="vertical-align:top;padding-right:18px;border-right:2px solid #3A5A40"><img src="${logoAbs}" alt="${brand}" width="68" height="68" style="display:block;object-fit:contain"/></td><td style="vertical-align:top;padding-left:18px;font-size:13px;line-height:1.5"><div style="font-family:'Cormorant Garamond',Georgia,serif;font-size:20px;color:#1C1917">${person.name}</div><div style="font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;font-size:14px;color:#3A5A40;margin-bottom:6px">${person.title} · ${person.creds}</div><div style="color:#9F5C45;letter-spacing:2px;text-transform:uppercase;font-size:10px;margin-bottom:6px">${brand}</div><div style="color:#57534E;font-size:12px"><a href="tel:${phone.replace(/\s/g, "")}" style="color:#1C1917;text-decoration:none">${phone}</a> · <a href="mailto:${email}" style="color:#1C1917;text-decoration:none">${email}</a></div><div style="color:#57534E;font-size:12px">${addr1}, ${addr2}</div><div style="margin-top:6px;font-size:12px"><a href="https://${DOMAIN}" style="color:#3A5A40;text-decoration:none;font-weight:600">${DOMAIN}</a></div></td></tr></tbody></table>`;
}

// — INSTAGRAM FEED PLANNER · 9 launch tiles —
export const SAGE_GRADIENT = `linear-gradient(135deg, ${IMPRINT_SAGE} 0%, ${IMPRINT_DEEP} 50%, ${IMPRINT_SAGE} 100%)`;
export const IVORY = "#FDFBF7";
export const GOLD_GRAD = "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)";

const FEED_TILES = [
  { id: "01-anchor",     kind: "anchor",  caption: "The anchor post — pin this." },
  { id: "02-quote",      kind: "quote",   eyebrow: "A reminder",     line1: "You're allowed",   line2: "to let this go." },
  { id: "03-service",    kind: "service", eyebrow: "We offer",        title: "Individual Therapy",  blurb: "Anxiety · Depression · Trauma · EMDR" },
  { id: "04-quote",      kind: "quote",   eyebrow: "A quiet truth",  line1: "Healing doesn't",  line2: "have a deadline." },
  { id: "05-visit",      kind: "visit",   eyebrow: "Visit",           title: "Highland Avenue",     blurb: "808 Highland Ave · 1st Floor\nWaterbury, CT 06708" },
  { id: "06-service",    kind: "service", eyebrow: "We offer",        title: "Couples & Family",    blurb: "Rebuild trust, communication, intimacy" },
  { id: "07-quote",      kind: "quote",   eyebrow: "Begin here",     line1: "A quiet space",   line2: "to let go." },
  { id: "08-service",    kind: "service", eyebrow: "We offer",        title: "Spa & Massage",       blurb: "Body-centred care · Somatic rituals" },
  { id: "09-cta",        kind: "cta",     eyebrow: "New patients",   line1: "Book your",       line2: "first session.", cta: "letgowelllness.com" },
];

const FeedTile = React.forwardRef(function FeedTile({ tile, brand, logoUrl }, ref) {
  // All tiles share the sage surface so the grid feels unified.
  const base = (
    <div
      ref={ref}
      data-testid={`feed-tile-${tile.id}`}
      className="relative overflow-hidden"
      style={{ width: "3in", height: "3in", background: SAGE_GRADIENT }}
    >
      {/* Brushed grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />
      {/* Inner content varies by kind */}
      <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center" style={{ padding: "0.3in" }}>
        {tile.kind === "anchor" && (
          <>
            {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "1.1in", h: "1.1in" }} />}
            <p className="font-serif uppercase mt-2" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: "0.36in", letterSpacing: "0.18em", lineHeight: 1 }}>Let Go</p>
            <p style={{ fontFamily: "'Sacramento', cursive", fontSize: "0.3in", letterSpacing: "0.04em", lineHeight: 1, background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", marginTop: "0.05in" }}>Wellness</p>
            <p className="uppercase mt-3" style={{ color: "rgba(253,251,247,0.6)", fontSize: "0.08in", letterSpacing: "0.34em" }}>Waterbury · CT</p>
          </>
        )}

        {tile.kind === "quote" && (
          <>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.34em", marginBottom: "0.2in" }}>{tile.eyebrow}</p>
            <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.32in", lineHeight: 1.25 }}>
              {tile.line1}<br />{tile.line2}
            </p>
            <div style={{ width: "0.4in", height: "1px", background: "#E8C669", margin: "0.18in 0 0" }} />
          </>
        )}

        {tile.kind === "service" && (
          <>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.34em" }}>{tile.eyebrow}</p>
            <p className="font-serif mt-3" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400, fontSize: "0.32in", lineHeight: 1.1, letterSpacing: "0.005em" }}>{tile.title}</p>
            <div style={{ width: "0.4in", height: "1px", background: "#E8C669", margin: "0.16in 0" }} />
            <p style={{ color: "rgba(253,251,247,0.8)", fontSize: "0.11in", lineHeight: 1.45, fontFamily: "Manrope, sans-serif" }}>{tile.blurb}</p>
          </>
        )}

        {tile.kind === "visit" && (
          <>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.34em" }}>{tile.eyebrow}</p>
            <p className="font-serif italic mt-3" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.28in", lineHeight: 1.15 }}>{tile.title}</p>
            <div style={{ width: "0.4in", height: "1px", background: "#E8C669", margin: "0.16in 0" }} />
            <p style={{ color: "rgba(253,251,247,0.85)", fontSize: "0.115in", lineHeight: 1.5, fontFamily: "Manrope, sans-serif", whiteSpace: "pre-line" }}>{tile.blurb}</p>
          </>
        )}

        {tile.kind === "cta" && (
          <>
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.34em" }}>{tile.eyebrow}</p>
            <p className="font-serif mt-2" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400, fontSize: "0.32in", lineHeight: 1.1 }}>
              {tile.line1}<br />{tile.line2}
            </p>
            <p className="uppercase mt-4" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.11in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>{tile.cta}</p>
          </>
        )}
      </div>
    </div>
  );
  return base;
});


// — INSTAGRAM CAROUSEL · "The first session" story · 8 swipeable slides —
const STORY_SLIDES = [
  { id: "01-title",   kind: "title",  eyebrow: "A short story",       title: "The first\nsession",  subtitle: "↓ swipe" },
  { id: "02-body",    kind: "body",   eyebrow: "1",  body: "Marcus almost didn't come.\n\nHe sat in his car for fourteen minutes, hand on the keys, thinking up reasons to drive away." },
  { id: "03-body",    kind: "body",   eyebrow: "2",  body: "But his daughter had asked him to try.\n\nJust once." },
  { id: "04-quote",   kind: "quote",  eyebrow: "3",  quote: "You came.\nThat's already a lot.", footer: "— his therapist, at the door" },
  { id: "05-body",    kind: "body",   eyebrow: "4",  body: "She didn't ask him what was wrong.\n\nShe asked: \"What's been the hardest week of the year for you?\"\n\nFor the first time in a long time, Marcus told the truth." },
  { id: "06-body",    kind: "body",   eyebrow: "5",  body: "He cried. He apologized for crying.\n\nShe said he didn't have to." },
  { id: "07-quote",   kind: "quote",  eyebrow: "6",  quote: "You don't have to\nfigure this out alone\nanymore.", footer: "I'll see you next week." },
  { id: "08-cta",     kind: "cta",    eyebrow: "Let Go Wellness", title: "The hardest part\nis walking in.", subtitle: "We make the rest gentle.", cta: "Book your first session · letgowelllness.com" },
];

const CarouselSlide = React.forwardRef(function CarouselSlide({ slide, brand, logoUrl }, ref) {
  return (
    <div
      ref={ref}
      data-testid={`carousel-slide-${slide.id}`}
      className="relative overflow-hidden"
      style={{ width: "3.6in", height: "3.6in", background: SAGE_GRADIENT }}
    >
      {/* Brushed grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />

      {/* Top brand bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between" style={{ padding: "0.32in 0.36in 0" }}>
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.085in", letterSpacing: "0.34em", fontFamily: "Manrope, sans-serif" }}>
          {brand || "Let Go Wellness"}
        </p>
        <p style={{ color: "rgba(253,251,247,0.45)", fontSize: "0.085in", letterSpacing: "0.18em", fontFamily: "Manrope, sans-serif" }}>
          {slide.eyebrow}
        </p>
      </div>

      {/* Slide content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ padding: "0.7in 0.45in" }}>
        {slide.kind === "title" && (
          <>
            {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.9in", h: "0.9in" }} />}
            <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.52in", lineHeight: 1.05, marginTop: "0.16in", whiteSpace: "pre-line" }}>
              {slide.title}
            </p>
            <div style={{ width: "0.5in", height: "1px", background: "#E8C669", margin: "0.22in 0 0.18in" }} />
            <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.1in", letterSpacing: "0.34em", fontFamily: "Manrope, sans-serif" }}>
              {slide.subtitle}
            </p>
          </>
        )}

        {slide.kind === "body" && (
          <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.22in", lineHeight: 1.5, whiteSpace: "pre-line", maxWidth: "2.8in" }}>
            {slide.body}
          </p>
        )}

        {slide.kind === "quote" && (
          <>
            <p style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.42in", fontFamily: "'Sacramento', cursive", lineHeight: 1.15, whiteSpace: "pre-line" }}>
              {slide.quote}
            </p>
            {slide.footer && (
              <>
                <div style={{ width: "0.4in", height: "1px", background: "rgba(232,198,105,0.5)", margin: "0.2in 0" }} />
                <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.75)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.14in", lineHeight: 1.4 }}>
                  {slide.footer}
                </p>
              </>
            )}
          </>
        )}

        {slide.kind === "cta" && (
          <>
            <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.36in", lineHeight: 1.15, whiteSpace: "pre-line" }}>
              {slide.title}
            </p>
            <div style={{ width: "0.5in", height: "1px", background: "#E8C669", margin: "0.22in 0" }} />
            <p className="font-serif" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.2in", lineHeight: 1.4 }}>
              {slide.subtitle}
            </p>
            <p className="uppercase mt-6" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.1in", letterSpacing: "0.26em", fontFamily: "Manrope, sans-serif" }}>
              {slide.cta}
            </p>
          </>
        )}
      </div>

      {/* Bottom slide number */}
      {slide.kind !== "title" && slide.kind !== "cta" && (
        <p className="absolute uppercase" style={{ bottom: "0.32in", left: 0, right: 0, textAlign: "center", color: "rgba(253,251,247,0.35)", fontSize: "0.075in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          ↓ swipe
        </p>
      )}
    </div>
  );
});


// — Reusable story poster canvas (1080×1920 portrait, sage gradient) —
export const StoryPosterCanvas = React.forwardRef(function StoryPosterCanvas(
  { brand, logoUrl, eyebrow, title, children, footerLine, footerCta },
  ref
) {
  return (
    <div
      ref={ref}
      className="border border-[#E2DCD0] relative"
      style={{
        width: "4in",
        height: "7.1in",
        background: SAGE_GRADIENT,
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
          opacity: 0.5,
        }}
      />
      {/* Top brand */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3" style={{ paddingTop: "0.32in" }}>
        {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.34in", h: "0.34in" }} />}
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.09in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          {eyebrow}
        </p>
      </div>
      {/* Title */}
      <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: "0.95in" }}>
        <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
          A short story
        </p>
        <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.36in", lineHeight: 1.05, marginTop: "0.08in", textAlign: "center", padding: "0 0.4in" }}>
          {title}
        </p>
        <div style={{ width: "0.45in", height: "1px", background: "#E8C669", marginTop: "0.16in" }} />
      </div>
      {/* Body */}
      <div className="absolute left-0 right-0" style={{ top: "2in", bottom: "1in", padding: "0 0.5in", overflow: "hidden" }}>
        {children}
      </div>
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center" style={{ paddingBottom: "0.34in" }}>
        <div style={{ width: "0.45in", height: "1px", background: "rgba(232,198,105,0.5)", marginBottom: "0.14in" }} />
        <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.16in", lineHeight: 1.3, textAlign: "center" }}>
          {footerLine}
        </p>
        <p className="uppercase mt-3" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.095in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>
          {footerCta}
        </p>
      </div>
    </div>
  );
});

// — Body paragraph styling shared by all posters —
export const StoryP = ({ children, mt }) => (
  <p
    className="font-serif"
    style={{
      color: IVORY,
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontWeight: 300,
      fontSize: "0.155in",
      lineHeight: 1.42,
      marginTop: mt ? mt : 0,
    }}
  >
    {children}
  </p>
);
export const Gold = ({ children }) => (
  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>{children}</span>
);
export const Em = ({ children }) => <em style={{ color: "rgba(253,251,247,0.95)" }}>{children}</em>;

// — TEEN STRESS SERIES · 10 short stories about teenage mental health —
export const TEEN_STORIES = [
  {
    id: "tn-01-perfect",
    slug: "the-b-plus",
    titleStr: "The B+ that broke her",
    eyebrow: "Let Go Wellness · Teens",
    title: <>The B+ that broke her</>,
    footerLine: <>She isn&apos;t lazy.<br />She&apos;s exhausted from being perfect.</>,
    footerCta: "Teen anxiety · letgowelllness.com",
    tag: "Perfectionism",
    body: (
      <>
        <StoryP>She got a B+.</StoryP>
        <StoryP mt="0.08in">She came home and didn&apos;t speak for an hour. Her mom found her under a blanket on the bathroom floor.</StoryP>
        <StoryP mt="0.08in">
          <Em>&ldquo;It&apos;s just one grade,&rdquo;</Em> her mom said gently.
        </StoryP>
        <StoryP mt="0.08in">
          <Em>&ldquo;It&apos;s not one grade,&rdquo;</Em> she whispered. <Em>&ldquo;It&apos;s the rest of my life.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          At therapy, the therapist asked, <Gold>&ldquo;Whose life are you trying to save?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">She didn&apos;t have an answer.</StoryP>
      </>
    ),
    caption: `She got a B+.

She came home and didn't speak for an hour.

"It's just one grade," her mom said.
"It's the rest of my life," she whispered.

Some teens aren't lazy — they're exhausted from being perfect.

If your kid is melting down over a grade that any adult would call "fine," it isn't about the grade. It's about who they think they're allowed to be when they're not perfect.

↓ Teen anxiety & perfectionism · letgowelllness.com

#teenanxiety #perfectionism #teenmentalhealth #parentingteens #anxietyinteens #academicpressure #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-02-likes",
    slug: "forty-seven-likes",
    titleStr: "Forty-seven likes",
    eyebrow: "Let Go Wellness · Teens",
    title: <>Forty-seven likes</>,
    footerLine: <>You are not what they liked.<br />You are not what they didn&apos;t.</>,
    footerCta: "Social media & self-worth · letgowelllness.com",
    tag: "Social Media",
    body: (
      <>
        <StoryP>She posted a photo at 8:14 p.m.</StoryP>
        <StoryP mt="0.08in">By 8:37, it had forty-seven likes. She knew, because she was counting.</StoryP>
        <StoryP mt="0.08in">By 9:15, she had deleted it.</StoryP>
        <StoryP mt="0.08in">
          In her therapist&apos;s office she said: <Em>&ldquo;I don&apos;t even like the photo. I just need other people to.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist asked, <Gold>&ldquo;What would it feel like to post something and not check?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">
          She laughed. Then went quiet. <Em>&ldquo;Honestly? Terrifying.&rdquo;</Em>
        </StoryP>
      </>
    ),
    caption: `She posted a photo at 8:14 p.m.
By 8:37, it had forty-seven likes. She knew, because she was counting.
By 9:15, she had deleted it.

"I don't even like the photo. I just need other people to."

Social media isn't the enemy. But it has become the mirror our teens check most — and it always lies.

If your teen is anxious about every post, every story, every read receipt — they don't need a lecture. They need someone trained to help them find their footing.

↓ Teen therapy · letgowelllness.com

#socialmediaandmentalhealth #teenanxiety #genz #digitalwellbeing #selfworth #teentherapy #parentingteens #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-03-groupchat",
    slug: "group-chat-quiet",
    titleStr: "When the group chat went quiet",
    eyebrow: "Let Go Wellness · Teens",
    title: <>When the group chat went quiet</>,
    footerLine: <>You are allowed to grieve a friendship<br />that didn&apos;t end with a fight.</>,
    footerCta: "Teen therapy · letgowelllness.com",
    tag: "Friendship Loss",
    body: (
      <>
        <StoryP>The group chat had been quiet for three days.</StoryP>
        <StoryP mt="0.08in">She kept scrolling up, re-reading what she&apos;d said. Trying to find the moment they decided.</StoryP>
        <StoryP mt="0.08in">
          At dinner, her dad said: <Em>&ldquo;Maya, you&apos;ve barely touched your food.&rdquo;</Em> She shrugged. He let it go.
        </StoryP>
        <StoryP mt="0.08in">
          In therapy she finally said it out loud: <Em>&ldquo;I think they don&apos;t want me anymore.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist asked, <Gold>&ldquo;When did being wanted start meaning so much?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">She thought a long time. <Em>&ldquo;Seventh grade.&rdquo;</Em></StoryP>
      </>
    ),
    caption: `The group chat had been quiet for three days.

She kept scrolling up, re-reading what she'd said — trying to find the moment they decided.

"I think they don't want me anymore."

A teen friendship that quietly fades is a real loss. There's no fight, no reason, no closure — just silence. And teens are not always given language for that kind of grief.

You are allowed to grieve a friendship that didn't end with a fight.

↓ Teen therapy · letgowelllness.com

#teenfriendships #socialanxiety #teenmentalhealth #friendshipbreakup #genz #parentingteens #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-04-identity",
    slug: "the-post-he-never-sent",
    titleStr: "The post he never sent",
    eyebrow: "Let Go Wellness · LGBTQ+ Teens",
    title: <>The post he never sent</>,
    footerLine: <>We hold space for who you are.<br />At your pace.</>,
    footerCta: "LGBTQ+ teen care · letgowelllness.com",
    tag: "Identity",
    body: (
      <>
        <StoryP>He typed it. Deleted it. Typed it again.</StoryP>
        <StoryP mt="0.08in">
          <Em>&ldquo;I think I might be —&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">He stopped there for six months.</StoryP>
        <StoryP mt="0.08in">
          The therapist didn&apos;t ask him to finish the sentence. She said,{" "}
          <Gold>&ldquo;You don&apos;t have to come out. You just have to come back to yourself.&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">He started crying before he understood why.</StoryP>
        <StoryP mt="0.08in">
          The next session, he came in wearing nail polish on one thumb. Just one. <Em>&ldquo;It&apos;s a start,&rdquo;</Em> he said.
        </StoryP>
      </>
    ),
    caption: `He typed it. Deleted it. Typed it again.

"I think I might be —"

He stopped there for six months.

Coming out is not a performance and it does not have to be on anyone else's timeline. Some teens need to come back to themselves before they say a single word out loud.

If your teen is figuring out who they are — they deserve a space that is patient, affirming, and trained to walk with them.

↓ LGBTQ+ teen affirming therapy · letgowelllness.com

#lgbtqyouth #queerteens #affirmingtherapy #teenidentity #lgbtqmentalhealth #safespace #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-05-bench",
    slug: "the-bench",
    titleStr: "The bench",
    eyebrow: "Let Go Wellness · Teens",
    title: <>The bench</>,
    footerLine: <>You are more than your stat line.<br />You always were.</>,
    footerCta: "Teen identity & pressure · letgowelllness.com",
    tag: "Identity / Sports",
    body: (
      <>
        <StoryP>He&apos;d been a starter for three seasons.</StoryP>
        <StoryP mt="0.08in">
          Now he sat at the end of the bench, towel over his head, hoping no one talked to him.
        </StoryP>
        <StoryP mt="0.08in">He didn&apos;t tell his parents. He didn&apos;t tell the team. He told his therapist.</StoryP>
        <StoryP mt="0.08in">
          <Em>&ldquo;My whole identity was that I was the kid who started.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist asked, <Gold>&ldquo;Who are you when you&apos;re not on the field?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">He didn&apos;t know. They spent eight sessions finding out.</StoryP>
      </>
    ),
    caption: `He'd been a starter for three seasons.
Now he sat at the end of the bench, towel over his head.

"My whole identity was that I was the kid who started."

When a teen's identity is tied to one thing — the team, the GPA, the role — losing it feels like losing themselves. They are not melodramatic. They just need help finding the parts of them that exist off the field.

↓ Teen identity & pressure · letgowelllness.com

#teenmentalhealth #studentathletes #identitytherapy #teenboys #parentingteens #athletementalhealth #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-06-college",
    slug: "safety-schools",
    titleStr: "The list of safety schools",
    eyebrow: "Let Go Wellness · Teens",
    title: <>The list of safety schools</>,
    footerLine: <>Your future is not<br />a single decision day.</>,
    footerCta: "College pressure & teens · letgowelllness.com",
    tag: "College Pressure",
    body: (
      <>
        <StoryP>She had thirty-one tabs open.</StoryP>
        <StoryP mt="0.08in">
          A spreadsheet. Three AP essays in progress. A college list color-coded by acceptance rate.
        </StoryP>
        <StoryP mt="0.08in">She hadn&apos;t eaten lunch in three weeks.</StoryP>
        <StoryP mt="0.08in">
          In her therapist&apos;s office she said: <Em>&ldquo;If I don&apos;t get in, my parents will be so disappointed.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist asked: <Gold>&ldquo;And what will you be?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">She didn&apos;t answer.</StoryP>
        <StoryP mt="0.08in">
          The therapist said, <Em>&ldquo;Today, let&apos;s just start with lunch.&rdquo;</Em>
        </StoryP>
      </>
    ),
    caption: `She had thirty-one tabs open.
A spreadsheet. Three AP essays in progress. A college list color-coded by acceptance rate.

She hadn't eaten lunch in three weeks.

"If I don't get in, my parents will be so disappointed."
"And what will you be?"

College admissions has become a mental health crisis disguised as ambition. Your future is not a single decision day. And you deserve to eat lunch.

↓ College-pressure therapy for teens · letgowelllness.com

#collegeadmissions #teenanxiety #aptest #seniorstress #parentingteens #teenmentalhealth #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-07-fridge",
    slug: "what-mom-didnt-ask",
    titleStr: "What mom didn't ask",
    eyebrow: "Let Go Wellness · Teens",
    title: <>What mom didn&apos;t ask</>,
    footerLine: <>Some conversations are easier<br />to write than to speak.</>,
    footerCta: "Family therapy · letgowelllness.com",
    tag: "Family",
    body: (
      <>
        <StoryP><Em>&ldquo;How was school?&rdquo;</Em> <Em>&ldquo;Fine.&rdquo;</Em></StoryP>
        <StoryP mt="0.06in"><Em>&ldquo;Any homework?&rdquo;</Em> <Em>&ldquo;Some.&rdquo;</Em></StoryP>
        <StoryP mt="0.06in"><Em>&ldquo;How was the test?&rdquo;</Em> <Em>&ldquo;Whatever.&rdquo;</Em></StoryP>
        <StoryP mt="0.08in">Three years of dinner.</StoryP>
        <StoryP mt="0.08in">
          In therapy he said, <Em>&ldquo;She never asks anything that actually matters.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist said, <Gold>&ldquo;What would you want her to ask?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">
          He thought a long time. <Em>&ldquo;If I was okay. Not how I did.&rdquo;</Em>
        </StoryP>
      </>
    ),
    caption: `"How was school?" "Fine."
"Any homework?" "Some."
"How was the test?" "Whatever."

Three years of dinner.

"She never asks anything that actually matters."
"What would you want her to ask?"
"If I was okay. Not how I did."

If you're a parent and "Fine" is the longest answer you get — you are not alone. Family therapy can help everyone find the right questions.

↓ Family therapy · letgowelllness.com

#familytherapy #parentingteens #teenmentalhealth #dinnertabletalk #parentchildrelationship #waterburyct #ctmoms #ctdads #letgowellness`,
  },
  {
    id: "tn-08-mirror",
    slug: "the-mirror-at-the-dance",
    titleStr: "The mirror at the dance",
    eyebrow: "Let Go Wellness · Teens",
    title: <>The mirror at the dance</>,
    footerLine: <>You don&apos;t have to fight<br />the mirror today.</>,
    footerCta: "Body image & teens · letgowelllness.com",
    tag: "Body Image",
    body: (
      <>
        <StoryP>She stood in the bathroom for twelve minutes.</StoryP>
        <StoryP mt="0.08in">
          She&apos;d lost the weight. She had the dress. She had the night. But the mirror gave her back someone she still didn&apos;t like.
        </StoryP>
        <StoryP mt="0.08in">She didn&apos;t go back out to the dance floor.</StoryP>
        <StoryP mt="0.08in">
          She told the therapist this six weeks later, almost as a side note.
        </StoryP>
        <StoryP mt="0.08in">
          The therapist asked, <Gold>&ldquo;Whose voice is in the mirror with you?&rdquo;</Gold>
        </StoryP>
        <StoryP mt="0.08in">She didn&apos;t have to think long. <Em>&ldquo;My fourteen-year-old self.&rdquo;</Em></StoryP>
      </>
    ),
    caption: `She stood in the bathroom for twelve minutes.
She'd lost the weight. She had the dress. She had the night.
But the mirror gave her back someone she still didn't like.

"Whose voice is in the mirror with you?"
"My fourteen-year-old self."

Body image healing isn't about loving every angle. It's about noticing whose voice is in the mirror — and gently asking her to sit down.

↓ Body image therapy for teens · letgowelllness.com

#bodyimage #teenbodyimage #disorderedeating #eatingdisorderrecovery #teengirls #teenmentalhealth #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-09-locker",
    slug: "the-locker",
    titleStr: "The locker she stopped opening",
    eyebrow: "Let Go Wellness · Teens",
    title: <>The locker she stopped opening</>,
    footerLine: <>You don&apos;t have to<br />carry it alone.</>,
    footerCta: "Bullying recovery · letgowelllness.com",
    tag: "Bullying",
    body: (
      <>
        <StoryP>She&apos;d stopped using her locker.</StoryP>
        <StoryP mt="0.08in">
          She just carried everything. Every book, every binder, every coat. A bag too heavy for her shoulder.
        </StoryP>
        <StoryP mt="0.08in">Her counselor noticed.</StoryP>
        <StoryP mt="0.08in">
          At therapy she said, <Em>&ldquo;If I don&apos;t open it, I don&apos;t have to see what they wrote.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist asked who else had seen it. <Em>&ldquo;Nobody. I cleaned it off before anyone could.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist said, <Gold>&ldquo;I am so sorry you&apos;ve been carrying this alone.&rdquo;</Gold>
        </StoryP>
      </>
    ),
    caption: `She'd stopped using her locker.

She just carried everything. Every book, every binder, every coat. A bag too heavy for her shoulder.

"If I don't open it, I don't have to see what they wrote."

Bullying isn't always loud. Sometimes it lives in a sharpie line your kid sees once and then carries silently for months.

If your teen seems to be carrying too much — literally or figuratively — please don't wait.

↓ Bullying recovery for teens · letgowelllness.com

#bullyingrecovery #teenmentalhealth #stopbullying #youarenotalone #teentherapy #parentingteens #waterburyct #ctteens #letgowellness`,
  },
  {
    id: "tn-10-screen",
    slug: "screen-down",
    titleStr: "3 a.m. with the screen down",
    eyebrow: "Let Go Wellness · Teens",
    title: <>3 a.m. with the screen down</>,
    footerLine: <>Sleep isn&apos;t lazy.<br />It&apos;s where you become yourself again.</>,
    footerCta: "Teen sleep & screen stress · letgowelllness.com",
    tag: "Sleep / Screens",
    body: (
      <>
        <StoryP>She put her phone face-down at 1 a.m. Promised herself.</StoryP>
        <StoryP mt="0.08in">At 1:14 she flipped it back over.</StoryP>
        <StoryP mt="0.08in">At 2:33 she watched the same video for the fifth time.</StoryP>
        <StoryP mt="0.08in">
          At 3 a.m. she finally cried because she was so tired and didn&apos;t know why she kept doing this.
        </StoryP>
        <StoryP mt="0.08in">
          In therapy she said, <Em>&ldquo;I just want to feel something other than this.&rdquo;</Em>
        </StoryP>
        <StoryP mt="0.08in">
          The therapist nodded. <Gold>&ldquo;Then let&apos;s find what your phone has been replacing.&rdquo;</Gold>
        </StoryP>
      </>
    ),
    caption: `She put her phone face-down at 1 a.m. Promised herself.
At 1:14 she flipped it back over.
At 2:33 she watched the same video for the fifth time.
At 3 a.m. she finally cried because she was so tired.

"I just want to feel something other than this."

A phone at 3 a.m. is rarely about the phone. It's about what the phone is replacing — silence, sadness, loneliness, anxiety.

Help your teen find what's underneath the screen. Sleep will follow.

↓ Teen sleep & screen-stress therapy · letgowelllness.com

#teensleep #screentime #digitalwellbeing #teenanxiety #phoneaddiction #genz #parentingteens #waterburyct #ctteens #letgowellness`,
  },
];

// — Instagram captions for every story poster, in suggested posting order —
export const STORY_CAPTIONS = [
  {
    title: "The first session",
    tag: "General Therapy",
    posterFile: "letgo-story-poster-first-session.png",
    caption: `Some people sit in the parking lot for fourteen minutes before they walk in.

Some people don't walk in at all.

This is a story for the ones who almost didn't come — and the therapists who quietly say, "you came, that's already a lot."

The hardest part is walking in. We make the rest gentle.

📍 808 Highland Ave · Waterbury, CT
📞 Office 203-437-8193
↓ Book a first session · letgowelllness.com

#mentalhealth #therapy #anxiety #firsttherapysession #youcame #waterburyct #ctmoms #ctmentalhealth #letgowellness #healingjourney`,
  },
  {
    title: "The pause between",
    tag: "Couples",
    posterFile: "letgo-story-poster-the-pause-between.png",
    caption: `You're not bad partners. You're two people grieving in opposite directions.

Couples don't usually arrive at therapy when things are easy. They arrive when the silence has become heavier than the fighting.

It's not a fix. It's a beginning.

If you're ready to turn back toward each other — we're here.

↓ Couples therapy · letgowelllness.com

#couplestherapy #marriage #relationships #emotionallyfocusedtherapy #waterburyct #connecticuttherapy #ctcouples #letgowellness #therapythatworks`,
  },
  {
    title: "What the body knew",
    tag: "Trauma / EMDR",
    posterFile: "letgo-story-poster-what-the-body-knew.png",
    caption: `Sometimes the body tells the truth before the mind is ready to hear it.

A racing heart at the microwave timer. A flinch at a closing door. Tears that arrive without warning.

You're not broken. You're carrying something your body remembers — and EMDR can help you put it down.

↓ Trauma therapy · letgowelllness.com

#emdr #traumarecovery #ptsd #somaticexperiencing #thebodykeepsthescore #traumatherapy #healingfromtrauma #waterburyct #ctmentalhealth #letgowellness`,
  },
  {
    title: "After the baby came home",
    tag: "Postpartum",
    posterFile: "letgo-story-poster-after-the-baby-came-home.png",
    caption: `She thought she'd be glowing.

Instead she stood at the kitchen sink at 3 a.m. wondering when her body had stopped feeling like her own.

It is exhausting to love something this much while you are this depleted.

You're not failing. You are this depleted.

Postpartum support, without the judgment.

↓ Maternal mental health · letgowelllness.com

#postpartum #postpartumdepression #ppa #newmom #motherhood #maternalmentalhealth #4thtrimester #ctmoms #waterburyct #letgowellness #youarenotfailing`,
  },
  {
    title: "The exit she didn't take",
    tag: "Burnout",
    posterFile: "letgo-story-poster-the-exit-she-didnt-take.png",
    caption: `She missed her exit by nine miles.

She didn't realize until the dashboard glowed quietly back at her and her phone, for once, did not.

"I don't want to die. I want to disappear for a while. There's a difference."

Yes. There is.

Burnout doesn't always look like burnout. Sometimes it just looks like driving past the exit.

↓ Burnout & anxiety support · letgowelllness.com

#burnout #burnoutrecovery #anxiety #highfunctioninganxiety #workstress #mentalhealthmatters #therapyworks #waterburyct #ctmentalhealth #letgowellness`,
  },
  {
    title: "The empty chair",
    tag: "Grief",
    posterFile: "letgo-story-poster-the-empty-chair.png",
    caption: `She still set two plates.

Eleven months in, and her hands still set the second plate every night.

Everyone kept gently asking when she'd "move on."

Her therapist said: not yet. And that's okay.

You don't have to be ready. You only have to be honest.

Grief doesn't run on anyone's calendar but yours.

↓ Grief counselling · letgowelllness.com

#grief #griefsupport #widow #loss #griefjourney #bereavement #griefcounseling #youdonthavetomoveon #waterburyct #ctmentalhealth #letgowellness`,
  },
  {
    title: "La primera cita",
    tag: "En Español",
    posterFile: "letgo-story-poster-la-primera-cita.png",
    caption: `Marcos casi no vino.

Se quedó sentado en su auto catorce minutos, con la mano en las llaves, pensando razones para irse.

Pero su hija le había pedido que lo intentara. Solo una vez.

La terapeuta le dijo: "Viniste. Eso ya es mucho."

No tienes que hacer esto sola. Estamos aquí — terapia en español, en Waterbury.

↓ Terapia en español · letgowelllness.com

#saludmental #terapia #terapiaenespañol #ansiedad #depresion #latinosenterapia #saludmentallatina #waterburyct #ctlatinos #letgowellness`,
  },
  {
    title: "The list at 2 a.m.",
    tag: "Anxiety",
    posterFile: "letgo-story-poster-the-list-at-2am.png",
    caption: `She kept a list under her pillow.

Things to worry about. Things to fix. Things she had probably already messed up.

At 2 a.m., she'd add a new line. Did I sound weird in that email to my boss?

She'd been doing this since she was nineteen. She was now thirty-four.

Her therapist didn't ask her to stop worrying. She asked, "What has the list taken from you?"

Sleep. Friendships. Tuesdays.

You can let your mind rest. It's been working overtime.

↓ Anxiety therapy · letgowelllness.com

#anxiety #generalizedanxiety #highfunctioninganxiety #overthinking #anxietyrelief #anxietysupport #cbt #mentalhealth #waterburyct #ctmentalhealth #letgowellness`,
  },
  {
    title: "She wouldn't look up",
    tag: "Teens",
    posterFile: "letgo-story-poster-she-wouldnt-look-up.png",
    caption: `She wouldn't look up.

Hood pulled over her hair. Phone in her lap. One earbud in, one out — just in case.

Her mom had brought her. Forced her, the way she'd put it on the drive over.

The therapist didn't ask her to take the hood off. She said, "You don't have to talk today. You just have to be here. That counts."

They listened to one song together. The whole song.

When it ended, the girl said, very quietly: "Nobody asks me what I listen to anymore."

The hood stayed up. But she looked up.

We meet teens where they are. Even if that's the corner of the couch.

↓ Teen therapy · letgowelllness.com

#teentherapy #adolescenttherapy #teenmentalhealth #genz #parentingteens #anxietyinteens #depressioninteens #waterburyct #ctteens #letgowellness`,
  },
  {
    title: "The fourth time he came back",
    tag: "Recovery",
    posterFile: "letgo-story-poster-the-fourth-time.png",
    caption: `The first time, he was angry.
The second time, he was ashamed.
The third time, he was silent.

The fourth time, he sat down and said, "I'm tired of starting over."

The therapist didn't ask why he had relapsed. She asked, "What was the longest you went?"

"Eleven months."

"Then you know how to do eleven months."

Recovery isn't a finish line. It's a Tuesday. And then another Tuesday.

If you've started over more times than you can count — we are still here for the next Tuesday.

↓ Addiction & recovery support · letgowelllness.com

#recovery #addictionrecovery #sobriety #relapse #sobercurious #addictionsupport #onedayatatime #aa #na #waterburyct #ctmentalhealth #letgowellness`,
  },
];

export default function Marketing() {
  const { site } = useSite();
  const [searchParams] = useSearchParams();
  const brand = site?.brand_name || "Let Go Wellness";
  const tagline = "a quiet space to let go.";
  const logoUrl = site?.brand_logo_url || "";
  const phone = site?.phone || "203-437-8193";
  const phone2 = site?.phone2 || "203-768-6969";
  const email = site?.email || "Letgowellness1@gmail.com";
  const addr1 = site?.address_line1 || "808 Highland Ave, 1st Fl";
  const addr2 = site?.address_line2 || "Waterbury, CT 06708";
  const hoursWd = site?.hours_weekday || "Mon–Fri · 8am — 8pm";
  const hoursWe = site?.hours_weekend || "Sat · 9am — 4pm";
  const services = site?.services || [];

  const flyerRef = useRef(null);
  const socialRef = useRef(null);
  const instagramRef = useRef(null);
  const storyPostRef = useRef(null);
  const feedTileRefs = useRef({});
  const carouselSlideRefs = useRef({});
  const storyPosterRef = useRef(null);
  const couplesPosterRef = useRef(null);
  const traumaPosterRef = useRef(null);
  const postpartumPosterRef = useRef(null);
  const burnoutPosterRef = useRef(null);
  const griefPosterRef = useRef(null);
  const spanishPosterRef = useRef(null);
  const anxietyPosterRef = useRef(null);
  const teensPosterRef = useRef(null);
  const recoveryPosterRef = useRef(null);
  const teenStressRefs = useRef({});
  const postcardFrontRef = useRef(null);
  const postcardBackRef = useRef(null);
  const decalRef = useRef(null);
  const intakeStickerRef = useRef(null);
  const cardRefs = useRef({});
  const midnightRefs = useRef({});
  const onyxRefs = useRef({});
  const forestRefs = useRef({});
  const imprintRefs = useRef({});
  const printerBriefRef = useRef(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [printTarget, setPrintTarget] = useState(null); // dom-class to isolate when printing
  const [busy, setBusy] = useState(null); // "pdf" | "zip" | null

  // Auto-download flow: when the URL contains ?download=anchor-id we wait for
  // refs to mount, then trigger the matching poster download. Used by the
  // Social Planner page to grab posters without navigating away.
  useEffect(() => {
    const targetAnchor = searchParams.get("download");
    if (!targetAnchor) return;
    let cancelled = false;
    const trigger = async () => {
      const posterAnchorMap = {
        "story-poster":            { ref: storyPosterRef,    file: "letgo-story-poster-first-session.png",        w: 1080, h: 1920 },
        "story-poster-couples":    { ref: couplesPosterRef,  file: "letgo-story-poster-the-pause-between.png",    w: 1080, h: 1920 },
        "story-poster-trauma":     { ref: traumaPosterRef,   file: "letgo-story-poster-what-the-body-knew.png",   w: 1080, h: 1920 },
        "story-poster-postpartum": { ref: postpartumPosterRef, file: "letgo-story-poster-after-the-baby-came-home.png", w: 1080, h: 1920 },
        "story-poster-burnout":    { ref: burnoutPosterRef,  file: "letgo-story-poster-the-exit-she-didnt-take.png", w: 1080, h: 1920 },
        "story-poster-grief":      { ref: griefPosterRef,    file: "letgo-story-poster-the-empty-chair.png",      w: 1080, h: 1920 },
        "story-poster-spanish":    { ref: spanishPosterRef,  file: "letgo-story-poster-la-primera-cita.png",      w: 1080, h: 1920 },
        "story-poster-anxiety":    { ref: anxietyPosterRef,  file: "letgo-story-poster-the-list-at-2am.png",      w: 1080, h: 1920 },
        "story-poster-teens":      { ref: teensPosterRef,    file: "letgo-story-poster-she-wouldnt-look-up.png",  w: 1080, h: 1920 },
        "story-poster-recovery":   { ref: recoveryPosterRef, file: "letgo-story-poster-the-fourth-time.png",      w: 1080, h: 1920 },
      };

      // Resolve the target node — supports both general and teen-stress posters.
      const resolveNode = () => {
        const mapped = posterAnchorMap[targetAnchor];
        if (mapped && mapped.ref?.current) {
          return { node: mapped.ref.current, file: mapped.file, w: mapped.w, h: mapped.h };
        }
        if (targetAnchor.startsWith("teen-tn-")) {
          const teenId = targetAnchor.replace("teen-", "");
          const node = teenStressRefs.current[teenId];
          const story = TEEN_STORIES.find((s) => s.id === teenId);
          if (node && story) {
            return { node, file: `letgo-teen-stress-${story.slug}.png`, w: 1080, h: 1920 };
          }
        }
        return null;
      };

      // Scroll the anchor into view so its canvas is laid out + fonts settle.
      const tryScroll = () => document.getElementById(targetAnchor)?.scrollIntoView({ block: "center" });

      // Poll for up to ~6 seconds — refs may not be ready until React finishes
      // mounting this very large page.
      let resolved = null;
      for (let i = 0; i < 30; i++) {
        if (cancelled) return;
        tryScroll();
        resolved = resolveNode();
        if (resolved) break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 200));
      }
      if (!resolved) {
        toast.error("Could not find that poster to download. Please open it manually on /marketing.");
        return;
      }

      // Give fonts/images a beat to settle after final scroll.
      await new Promise((r) => setTimeout(r, 500));
      if (cancelled) return;

      try {
        await downloadPosterAtSize(resolved.node, resolved.file, resolved.w, resolved.h);
        try {
          window.opener?.postMessage({ type: "letgo:downloaded", anchor: targetAnchor }, "*");
          window.parent?.postMessage({ type: "letgo:downloaded", anchor: targetAnchor }, "*");
        } catch (_) { /* ignore postMessage failures */ }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Auto-download failed", e);
        toast.error("Download failed. Try again or open the marketing page manually.");
      }
    };
    trigger();
    return () => { cancelled = true; };
  }, [searchParams]);

  const doPrint = (cls) => {
    setPrintTarget(cls);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintTarget(null), 300);
    }, 50);
  };

  // Snapshot every marketing asset into PNGs (4× pixel ratio ≈ 300 dpi)
  const captureAll = async () => {
    const assets = [
      { name: "01-business-card-waleska-front", node: imprintRefs.current["waleska-front"], w: 3.5, h: 2 },
      { name: "02-business-card-waleska-back",  node: imprintRefs.current["waleska-back"],  w: 3.5, h: 2 },
      { name: "03-business-card-max-front",     node: imprintRefs.current["max-front"],     w: 3.5, h: 2 },
      { name: "04-business-card-max-back",      node: imprintRefs.current["max-back"],      w: 3.5, h: 2 },
      { name: "05-printer-brief",               node: printerBriefRef.current,              w: 8.5, h: 11 },
      { name: "06-letterhead",                  node: null,                                 w: 8.5, h: 11 }, // letterhead is rendered on /marketing/compare; bundle skips if no ref
      { name: "07-flyer",                       node: flyerRef.current,                     w: 8.5, h: 11 },
      { name: "08-social-square",               node: socialRef.current,                    w: 5.4, h: 5.4 },
      { name: "08b-instagram-profile",          node: instagramRef.current,                 w: 3.5, h: 3.5 },
      { name: "08c-story-post-invitation",      node: storyPostRef.current,                 w: 5.4, h: 5.4 },
      { name: "09-postcard-front",              node: postcardFrontRef.current,             w: 6,   h: 4 },
      { name: "10-postcard-back",               node: postcardBackRef.current,              w: 6,   h: 4 },
      { name: "11-window-decal",                node: decalRef.current,                     w: 6,   h: 6 },
    ];
    const out = [];
    for (const a of assets) {
      if (!a.node) continue;
      // Render at print-quality 300 DPI by default (w * 300 px wide). Background
      // is the asset's natural surface so anti-aliased edges blend correctly.
      const widthPx = Math.round(a.w * 300);
      const heightPx = Math.round(a.h * 300);
      // eslint-disable-next-line no-await-in-loop
      const dataUrl = await nodeToHdDataUrl(a.node, widthPx, heightPx, a.bg || "#FDFBF7");
      out.push({ ...a, dataUrl });
    }
    return out;
  };

  const downloadFullKitPdf = async () => {
    if (busy) return;
    setBusy("pdf");
    const t = toast.loading("Building your marketing kit PDF…");
    try {
      const assets = await captureAll();
      // Letter-sized pages (8.5 × 11), each asset centred at its true print size.
      const pdf = new jsPDF({ unit: "in", format: "letter", orientation: "portrait", compress: true });
      assets.forEach((a, i) => {
        if (i > 0) {
          const orient = a.w > a.h ? "landscape" : "portrait";
          pdf.addPage("letter", orient);
        } else if (a.w > a.h) {
          pdf.deletePage(1);
          pdf.addPage("letter", "landscape");
        }
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const x = (pageW - a.w) / 2;
        const y = (pageH - a.h) / 2;
        pdf.addImage(a.dataUrl, "PNG", x, y, a.w, a.h, undefined, "FAST");
        // Footer caption
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(`${brand} · ${a.name} · ${a.w}″ × ${a.h}″`, pageW / 2, pageH - 0.3, { align: "center" });
      });
      pdf.save("letgo-wellness-marketing-kit.pdf");
      toast.success("PDF ready — check your downloads.", { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Could not build PDF. Try again.", { id: t });
    } finally {
      setBusy(null);
    }
  };

  const downloadFullKitZip = async () => {
    if (busy) return;
    setBusy("zip");
    const t = toast.loading("Packaging PNGs into a ZIP…");
    try {
      const assets = await captureAll();
      const zip = new JSZip();
      const folder = zip.folder("letgo-wellness-marketing-kit");
      assets.forEach((a) => {
        const base64 = a.dataUrl.split(",")[1];
        folder.file(`${a.name}.png`, base64, { base64: true });
      });
      // Bundle a README
      folder.file(
        "README.txt",
        `${brand} — Marketing Kit\n\n` +
        assets.map((a) => `• ${a.name}.png — ${a.w}″ × ${a.h}″ (300 dpi)`).join("\n") +
        `\n\nAll files are print-ready PNGs at 300 dpi.\n` +
        `Hand these to any local printer or upload to Vistaprint / Moo / StickerYou.\n` +
        `Domain: https://${DOMAIN}\n`
      );
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "letgo-wellness-marketing-kit.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP ready — check your downloads.", { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Could not build ZIP. Try again.", { id: t });
    } finally {
      setBusy(null);
    }
  };

  // ── Printer Pack — single ZIP with SVG + True Vector PDF for EVERY business card.
  // One file the user can attach to a single email to the printer (instead of
  // four: 2 SVGs × 2 PDFs). Resolution-independent vector formats only.
  // Returns the generated Blob so callers can re-use it (e.g. email flow).
  const buildPrinterPackBlob = async () => {
    const zip = new JSZip();
    const folder = zip.folder("letgo-wellness-printer-pack");
    const lines = [`${brand} — Printer Pack`, "", "Vector assets for commercial printing — every print asset as SVG + true-vector PDF."];

    for (const person of PEOPLE) {
      const safe = person.key;

      // eslint-disable-next-line no-await-in-loop
      const pdfBlob = await buildBusinessCardVectorPdf({
        person,
        brand,
        phone,
        phone2,
        email,
        addr1,
        addr2,
        logoUrl,
        domain: DOMAIN,
        domainUrl: DOMAIN_URL,
        returnBlob: true,
      });
      if (pdfBlob) {
        folder.file(`business-cards/${safe}-card-vector.pdf`, pdfBlob);
        lines.push(`• business-cards/${safe}-card-vector.pdf — hand-coded true vector PDF (front + back)`);
      }

      // eslint-disable-next-line no-await-in-loop
      const svgFront = await nodeToSvgString(imprintRefs.current[`${person.key}-front`]);
      if (svgFront) {
        folder.file(`business-cards/${safe}-card-front.svg`, svgFront);
        lines.push(`• business-cards/${safe}-card-front.svg — scalable vector front`);
      }
      // eslint-disable-next-line no-await-in-loop
      const svgBack = await nodeToSvgString(imprintRefs.current[`${person.key}-back`]);
      if (svgBack) {
        folder.file(`business-cards/${safe}-card-back.svg`, svgBack);
        lines.push(`• business-cards/${safe}-card-back.svg — scalable vector back`);
      }
    }

    // ── Flyer — true-vector PDF + SVG ──
    const flyerPdf = await buildFlyerVectorPdf({
      brand,
      phone,
      email,
      addr1,
      addr2,
      services,
      logoUrl,
      hoursWd,
      hoursWe,
      domain: DOMAIN,
      domainUrl: DOMAIN_URL,
      returnBlob: true,
    });
    if (flyerPdf) {
      folder.file(`flyer/letgo-flyer-vector.pdf`, flyerPdf);
      lines.push(`• flyer/letgo-flyer-vector.pdf — true vector flyer (8.5\" × 11\")`);
    }
    const flyerSvg = await nodeToSvgString(flyerRef.current);
    if (flyerSvg) {
      folder.file(`flyer/letgo-flyer.svg`, flyerSvg);
      lines.push(`• flyer/letgo-flyer.svg — scalable vector flyer`);
    }

    // ── Postcard — true-vector PDF + SVG (front + back) ──
    const postcardPdf = await buildPostcardVectorPdf({
      brand,
      phone,
      addr1,
      addr2,
      logoUrl,
      domain: DOMAIN,
      domainUrl: DOMAIN_URL,
      returnBlob: true,
    });
    if (postcardPdf) {
      folder.file(`postcard/letgo-postcard-vector.pdf`, postcardPdf);
      lines.push(`• postcard/letgo-postcard-vector.pdf — true vector postcard (6\" × 4\", front + back)`);
    }
    const postcardFrontSvg = await nodeToSvgString(postcardFrontRef.current);
    if (postcardFrontSvg) {
      folder.file(`postcard/letgo-postcard-front.svg`, postcardFrontSvg);
      lines.push(`• postcard/letgo-postcard-front.svg — scalable vector postcard front`);
    }
    const postcardBackSvg = await nodeToSvgString(postcardBackRef.current);
    if (postcardBackSvg) {
      folder.file(`postcard/letgo-postcard-back.svg`, postcardBackSvg);
      lines.push(`• postcard/letgo-postcard-back.svg — scalable vector postcard back`);
    }

    folder.file(
      "README.txt",
      lines.join("\n") +
        "\n\n" +
        "Specs\n" +
        "─────\n" +
        "• Business cards: 3.5\" × 2\" · 32pt cotton or duplexed stock\n" +
        "• Postcard: USPS 6\" × 4\" · 14pt or heavier cardstock · full-bleed front\n" +
        "• Flyer: US Letter 8.5\" × 11\" · 100 lb matte or silk text · full-bleed if possible\n" +
        "• Bleed: extend solid background fields 0.125\" past trim\n" +
        "• Finish: blind deboss on \"LET GO\" + gold foil on \"Wellness\" and the back-side logo\n" +
        "• Color reference: sage #3A5A40 (CMYK 67 / 39 / 71 / 24), deep sage #243B2C, foil = matte gold\n" +
        "\n" +
        "File usage\n" +
        "──────────\n" +
        "• Vector PDFs: drop directly into any RIP / press workflow. Text and QR remain crisp at any scale.\n" +
        "• SVGs: useful for InDesign / Illustrator imports if you need to edit color or add bleed marks.\n" +
        "\n" +
        `Domain: https://${DOMAIN}\n`
    );

    return zip.generateAsync({ type: "blob" });
  };

  const downloadPrinterPackZip = async () => {
    if (busy) return;
    setBusy("printer-pack");
    const t = toast.loading("Building printer pack (SVG + true-vector PDF)…");
    try {
      const blob = await buildPrinterPackBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "letgo-wellness-printer-pack.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Printer pack ready — one ZIP, everything vector.", { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Could not build printer pack. Try again.", { id: t });
    } finally {
      setBusy(null);
    }
  };

  // Same ZIP, but also opens the visitor's email client with a printer-ready
  // body that lists every file + the press specs. User attaches the ZIP from
  // their Downloads folder and hits send.
  const emailPrinterPack = async () => {
    if (busy) return;
    setBusy("printer-pack");
    const t = toast.loading("Bundling printer pack and opening your email…");
    try {
      const blob = await buildPrinterPackBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "letgo-wellness-printer-pack.zip";
      a.click();
      URL.revokeObjectURL(url);

      const subject = encodeURIComponent("Print order — Let Go Wellness · vector printer pack");
      const body = encodeURIComponent(
        `Hi,\n\n` +
          `Please find attached the printer pack for Let Go Wellness. All assets are vector (SVG + hand-coded true-vector PDFs).\n\n` +
          `Files inside letgo-wellness-printer-pack.zip:\n` +
          `  • business-cards/waleska-card-vector.pdf — true vector, front + back\n` +
          `  • business-cards/waleska-card-front.svg & waleska-card-back.svg\n` +
          `  • business-cards/max-card-vector.pdf — true vector, front + back\n` +
          `  • business-cards/max-card-front.svg & max-card-back.svg\n` +
          `  • flyer/letgo-flyer-vector.pdf — true vector flyer\n` +
          `  • flyer/letgo-flyer.svg\n` +
          `  • postcard/letgo-postcard-vector.pdf — true vector postcard (front + back)\n` +
          `  • postcard/letgo-postcard-front.svg & letgo-postcard-back.svg\n` +
          `  • README.txt — full press specs\n\n` +
          `Specs\n` +
          `─────\n` +
          `• Business cards: 3.5" × 2" trim · 0.125" bleed · 32pt cotton or duplexed stock\n` +
          `• Postcard: USPS 6" × 4" · 14pt or heavier cardstock · full-bleed front\n` +
          `• Flyer: US Letter 8.5" × 11" · 100 lb matte/silk text · full bleed\n` +
          `• Finish: blind deboss on "LET GO" + gold foil on "Wellness" and the back-side logo\n` +
          `• Colour: sage #3A5A40 (CMYK 67 / 39 / 71 / 24), deep sage #243B2C, foil = matte gold\n` +
          `• Quantities (please confirm):\n` +
          `    – Business cards (Waleska): __\n` +
          `    – Business cards (Max): __\n` +
          `    – Postcards: __\n` +
          `    – Flyers: __\n\n` +
          `Please confirm receipt of the ZIP and let me know the expected turnaround.\n\n` +
          `Thank you,\n${brand}\nhttps://${DOMAIN}`
      );
      // Slight delay so the download dialog opens before the mailto handler takes over
      setTimeout(() => {
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }, 600);
      toast.success("ZIP downloaded — attach it to the email that just opened.", { id: t });
    } catch (err) {
      console.error(err);
      toast.error("Could not build printer pack. Try again.", { id: t });
    } finally {
      setBusy(null);
    }
  };

  const copySig = async (person) => {
    const html = buildSignatureHtml({ person, brand, logoUrl, phone, email, addr1, addr2 });
    try {
      // Rich-text copy
      const blob = new Blob([html], { type: "text/html" });
      const text = new Blob([html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()], { type: "text/plain" });
      // eslint-disable-next-line no-undef
      await navigator.clipboard.write([new ClipboardItem({ "text/html": blob, "text/plain": text })]);
    } catch {
      await navigator.clipboard.writeText(html);
    }
    setCopiedKey(person.key);
    setTimeout(() => setCopiedKey(null), 2200);
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="marketing-page">
      <Navbar />

      <section className="pt-48 pb-10 print:hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/share" data-testid="marketing-back" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors mb-10">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to share
          </Link>
          <p className="overline text-[#9F5C45] mb-6">Marketing Package</p>
          <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-6 max-w-4xl">
            Print-ready, on-brand, <em className="italic text-[#3A5A40]">ready to share</em>.
          </h1>
          <p className="text-[#57534E] text-base md:text-lg leading-relaxed max-w-2xl mb-4">
            Business cards, flyers, social posts and email signatures — all generated from your live
            clinic details. Download as high-resolution PNGs (300 dpi) or print directly to PDF.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href="#business-cards" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Business Cards</a>
            <a href="#midnight-cards" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Midnight (Navy)</a>
            <a href="#onyx-cards" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Onyx (Black + Gold)</a>
            <a href="#forest-cards" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Forest (Sage + Gold)</a>
            <a href="#flyer" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Flyer</a>
            <a href="#social" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Social Post</a>
            <a href="#instagram-profile" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">IG Profile</a>
            <a href="#story-post" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Story Post</a>
            <a href="#story-poster" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Story Poster</a>
            <a href="#story-poster-couples" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Couples Story</a>
            <a href="#story-poster-trauma" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Trauma Story</a>
            <a href="#story-poster-postpartum" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Postpartum</a>
            <a href="#story-poster-burnout" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Burnout</a>
            <a href="#story-poster-grief" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Grief</a>
            <a href="#story-poster-spanish" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">En Español</a>
            <a href="#story-poster-anxiety" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Anxiety</a>
            <a href="#story-poster-teens" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Teens</a>
            <a href="#story-poster-recovery" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Recovery</a>
            <a href="#teen-stress-series" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em] bg-[#3A5A40]/10 text-[#3A5A40]">Teen Stress (10)</a>
            <a href="#captions-pack" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Captions</a>
            <Link to="/socials" className="px-4 py-2 btn-primary text-xs uppercase tracking-[0.22em] bg-[#3A5A40] text-[#FDFBF7]" data-testid="link-social-planner">Plan Posts →</Link>
            <Link to="/spa/marketing" className="px-4 py-2 btn-primary text-xs uppercase tracking-[0.22em] bg-[#9F5C45] text-[#FDFBF7]" data-testid="link-spa-marketing">Spa Kit →</Link>
            <a href="#feed-planner" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Feed (9 Tiles)</a>
            <a href="#story-carousel" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Story Carousel</a>
            <a href="#postcard" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Postcard</a>
            <a href="#decal" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Window Decal</a>
            <a href="#signature" className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em]">Email Signature</a>
            <Link
              to="/marketing/compare"
              data-testid="open-compare"
              className="px-4 py-2 btn-primary text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2"
            >
              Compare card designs
            </Link>
            <Link
              to="/marketing/review"
              data-testid="open-review"
              className="px-4 py-2 btn-ghost text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2"
            >
              Review all <ArrowLeft className="w-3 h-3 rotate-180" />
            </Link>
          </div>

          {/* Bulk download — everything in one file */}
          <div className="mt-10 border border-[#3A5A40]/30 bg-[#F4F1EA] p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="overline text-[#9F5C45] mb-2">Full marketing kit</p>
              <h3 className="font-serif text-2xl md:text-3xl text-[#1C1917]">
                Save everything as one file
              </h3>
              <p className="text-[#57534E] mt-2 text-sm md:text-base max-w-xl">
                Compile every asset — both business cards, flyer, social post, postcard and window decal — into a single
                multi-page PDF you can email to a print shop, or a ZIP of high-res PNGs.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={downloadFullKitPdf}
                disabled={!!busy}
                data-testid="download-full-pdf"
                className="inline-flex items-center gap-2 px-5 py-3 btn-primary text-xs tracking-[0.18em] uppercase disabled:opacity-60"
              >
                {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                {busy === "pdf" ? "Building…" : "Download full kit · PDF"}
              </button>
              <button
                onClick={downloadFullKitZip}
                disabled={!!busy}
                data-testid="download-full-zip"
                className="inline-flex items-center gap-2 px-5 py-3 btn-ghost text-xs tracking-[0.18em] uppercase disabled:opacity-60"
              >
                {busy === "zip" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {busy === "zip" ? "Packaging…" : "Download · PNG ZIP"}
              </button>
            </div>
          </div>

          {/* Printer-ready vector pack — bundles SVG + true-vector PDF for every print asset */}
          <div className="mt-4 border-2 border-[#3A5A40] bg-[#3A5A40] text-[#FDFBF7] p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="overline text-[#E8C669] mb-2">Printer Pack · vector only</p>
              <h3 className="font-serif text-2xl md:text-3xl">
                One ZIP for your printer
              </h3>
              <p className="text-[#FDFBF7]/85 mt-2 text-sm md:text-base max-w-xl">
                Both business cards (front + back), the flyer <em className="not-italic text-[#E8C669]">and</em> the postcard — every asset as a
                hand-coded true-vector PDF + an editable SVG, plus a printer-spec README. Download the ZIP, or have us
                open an email with the printer brief pre-filled.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={downloadPrinterPackZip}
                disabled={!!busy}
                data-testid="download-printer-pack"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#E8C669] text-[#243B2C] hover:bg-[#FDFBF7] text-xs tracking-[0.18em] uppercase disabled:opacity-60 transition-colors"
              >
                {busy === "printer-pack" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy === "printer-pack" ? "Bundling…" : "Download printer pack · ZIP"}
              </button>
              <button
                onClick={emailPrinterPack}
                disabled={!!busy}
                data-testid="email-printer-pack"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#E8C669] text-[#E8C669] hover:bg-[#E8C669] hover:text-[#243B2C] text-xs tracking-[0.18em] uppercase disabled:opacity-60 transition-colors"
              >
                <Mail className="w-4 h-4" /> Email printer pack
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* — SIZING PICKER (try different sizes with Pinyon Script) — */}
        <section className="border-t border-[#E2DCD0] pt-16 pb-16" data-testid="size-picker">
          <div className="mb-10">
            <p className="overline text-[#9F5C45] mb-3">Sizing</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] tracking-tight">
              How big should it sit?
            </h2>
            <p className="text-[#57534E] mt-3 max-w-xl text-sm md:text-base">
              Four size relationships between the logo and the Pinyon script.
              Reply with the size — <em>Compact · Balanced · Bold · Statement</em> — and I&apos;ll lock it in.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              { name: "Compact", text: "0.62in", logo: "0.7in", note: "Tighter, more breathing room around it" },
              { name: "Balanced", text: "0.78in", logo: "0.9in", note: "Current default" },
              { name: "Bold", text: "0.95in", logo: "1.05in", note: "Maximum presence, fills the card" },
              { name: "Statement", text: "1.1in",  logo: "1.2in",  note: "Audacious — edge-to-edge ceremony" },
            ].map((opt) => (
              <div key={opt.name} className="space-y-3" data-testid={`size-${opt.name.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <p className="overline text-[#3A5A40]">{opt.name}</p>
                  <p className="text-xs italic text-[#57534E]">{opt.note}</p>
                </div>
                <div className="border border-[#E2DCD0]">
                  <ImprintCardFront
                    person={PEOPLE[0]}
                    brand={brand}
                    logoUrl={logoUrl}
                    scriptFont="Pinyon Script"
                    textSize={opt.text}
                    logoSize={opt.logo}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* — BUSINESS CARDS · Sage Imprint (debossed sage + gold-foil logo) — */}
        <div id="business-cards" />
        {PEOPLE.map((person, idx) => (
          <AssetSection
            key={person.key}
            id={`card-${person.key}`}
            eyebrow={`Business Card · ${idx + 1} of ${PEOPLE.length}`}
            title={person.name}
            sub={`${person.title} · ${person.creds}. Standard US size — 3.5″ × 2″. Debossed wordmark on sage with gold-foil logo. Specify "blind deboss + gold foil stamp" with your printer; 32pt cotton or duplexed stock is ideal.`}
            downloadLabel="Download PDF (front + back)"
            onDownload={async () => {
              await downloadAsPDF(imprintRefs.current[`${person.key}-front`], `letgo-card-${person.key}-front.pdf`, 3.5, 2, "#3A5A40");
              await downloadAsPDF(imprintRefs.current[`${person.key}-back`], `letgo-card-${person.key}-back.pdf`, 3.5, 2, "#3A5A40");
            }}
            onVectorPdf={async () => {
              const t = toast.loading("Drawing your true-vector business card…");
              try {
                await buildBusinessCardVectorPdf({
                  person,
                  brand,
                  phone,
                  phone2,
                  email,
                  addr1,
                  addr2,
                  logoUrl,
                  domain: DOMAIN,
                  domainUrl: DOMAIN_URL,
                  filename: `letgo-card-${person.key}-vector.pdf`,
                });
                toast.success("True-vector PDF saved. Text and QR are pure vector — perfect for any printer.", { id: t });
              } catch (e) {
                console.error(e);
                toast.error("Could not build vector PDF. Try again.", { id: t });
              }
            }}
            onDownloadSvg={async () => {
              const t = toast.loading("Exporting SVG (front + back)…");
              try {
                await downloadAsSvg(imprintRefs.current[`${person.key}-front`], `letgo-card-${person.key}-front.svg`);
                await downloadAsSvg(imprintRefs.current[`${person.key}-back`], `letgo-card-${person.key}-back.svg`);
                toast.success("SVGs saved — scalable to any size.", { id: t });
              } catch (e) {
                console.error(e);
                toast.error("Could not build SVG. Try again.", { id: t });
              }
            }}
            onEmail={() =>
              emailToPrinter(imprintRefs.current[`${person.key}-front`], `letgo-card-${person.key}-front`, 3.5, 2, {
                backgroundColor: "#3A5A40",
                subject: `Print order — Let Go Wellness business card · ${person.name}`,
                notes: `Standard US business card · 3.5" × 2" · Blind deboss + gold foil stamp recommended. Quantity: __. Please confirm receipt.`,
              })
            }
            onPrint={() => doPrint(`print-card-${person.key}`)}
          >
            <div className={`flex flex-wrap gap-8 print-card-${person.key}`}>
              <div className="space-y-2">
                <p className="overline text-[#57534E]">Front</p>
                <div
                  ref={(el) => (imprintRefs.current[`${person.key}-front`] = el)}
                  data-testid={`card-front-${person.key}`}
                  className="border border-[#E2DCD0]"
                >
                  <ImprintCardFront person={person} brand={brand} logoUrl={logoUrl} />
                </div>
              </div>
              <div className="space-y-2">
                <p className="overline text-[#57534E]">Back</p>
                <div
                  ref={(el) => (imprintRefs.current[`${person.key}-back`] = el)}
                  data-testid={`card-back-${person.key}`}
                  className="border border-[#E2DCD0]"
                >
                  <ImprintCardBack
                    person={person}
                    brand={brand}
                    phone={phone}
                    phone2={phone2}
                    email={email}
                    addr1={addr1}
                    addr2={addr2}
                    logoUrl={logoUrl}
                  />
                </div>
              </div>
            </div>
          </AssetSection>
        ))}

        {/* — PRINTER BRIEF (one-page spec sheet for any print shop) — */}
        <AssetSection
          id="printer-brief"
          eyebrow="For your printer"
          title="One-page printer brief"
          sub="A complete technical spec sheet you can email to any print shop — dimensions, stock, color codes, foil and deboss specs, bleed marks. Saves hours of back-and-forth. Download as PNG or print to PDF."
          onDownload={() => downloadAsPDF(printerBriefRef.current, "letgo-printer-brief.pdf", 8.5, 11, "#FDFBF7")}
          onDownloadSvg={() => downloadAsSvg(printerBriefRef.current, "letgo-printer-brief.svg")}
          onEmail={() => emailToPrinter(printerBriefRef.current, "letgo-printer-brief", 8.5, 11, { subject: "Print specifications — Let Go Wellness brand kit", notes: "Letter size 8.5\" × 11\" · please print at 100% scale." })}
          downloadLabel="Download PDF"
          onPrint={() => doPrint("print-brief")}
        >
          <div className="print-brief">
            <div
              ref={printerBriefRef}
              data-testid="printer-brief-canvas"
              className="border border-[#E2DCD0] origin-top-left"
              style={{ transform: "scale(0.55)", transformOrigin: "top left", width: "8.5in", height: "11in", marginBottom: "-5in", marginRight: "-3.8in" }}
            >
              <PrinterBrief brand={brand} phone={phone} email={email} />
            </div>
          </div>
        </AssetSection>

        {/* — FLYER — */}
        <AssetSection
          id="flyer"
          eyebrow="Marketing Flyer"
          title="Waiting-room & community flyer"
          sub="US Letter (8.5″ × 11″). Drop these in coffee shops, primary-care offices, gyms — anywhere people need a quiet way in."
          onDownload={() => downloadAsPDF(flyerRef.current, "letgo-flyer.pdf", 8.5, 11, "#FDFBF7")}
          onVectorPdf={async () => {
            const t = toast.loading("Drawing your true-vector flyer…");
            try {
              await buildFlyerVectorPdf({
                brand,
                phone,
                email,
                addr1,
                addr2,
                services,
                logoUrl,
                hoursWd,
                hoursWe,
                domain: DOMAIN,
                domainUrl: DOMAIN_URL,
                filename: "letgo-flyer-vector.pdf",
              });
              toast.success("True-vector flyer saved — text and QR are pure vector.", { id: t });
            } catch (e) {
              console.error(e);
              toast.error("Could not build vector flyer. Try again.", { id: t });
            }
          }}
          onDownloadSvg={() => downloadAsSvg(flyerRef.current, "letgo-flyer.svg")}
          onEmail={() => emailToPrinter(flyerRef.current, "letgo-flyer", 8.5, 11, { subject: "Print order — Let Go Wellness flyer", notes: "Letter size 8.5\" × 11\" · please print at 100% scale, full bleed if possible. Quantity: __." })}
          downloadLabel="Download PDF"
          onPrint={() => doPrint("print-flyer")}
        >
          <div className="print-flyer">
            <div
              ref={flyerRef}
              data-testid="flyer-canvas"
              className="border border-[#E2DCD0] origin-top-left"
              style={{ transform: "scale(0.55)", transformOrigin: "top left", width: "8.5in", height: "11in", marginBottom: "-5in", marginRight: "-3.8in" }}
            >
              <ThemedFlyer
                theme={THEMES.imprint}
                brand={brand}
                logoUrl={logoUrl}
                phone={phone}
                email={email}
                addr1={addr1}
                addr2={addr2}
                services={services}
              />
            </div>
          </div>
        </AssetSection>

        {/* — SOCIAL POST — */}
        <AssetSection
          id="social"
          eyebrow="Social Media Post"
          title="Square post · 1080 × 1080"
          sub="Drop into Instagram, Facebook, LinkedIn. Exported PNG is high-res (~2000 × 2000) so it stays crisp."
          onDownload={() => downloadAtSize(socialRef.current, "letgo-social-square.png", 1080, 1080, "#243B2C")}
          onPrint={() => doPrint("print-social")}
        >
          <div className="print-social">
            <div ref={socialRef} data-testid="social-canvas" className="border border-[#E2DCD0]">
              <ThemedSocialPost
                theme={THEMES.imprint}
                brand={brand}
                logoUrl={logoUrl}
              />
            </div>
          </div>
        </AssetSection>

        {/* — STORY POSTER · full short story on one image — */}
        <AssetSection
          id="story-poster"
          eyebrow="Instagram · Story Poster"
          title="The first session · single image"
          sub="The full short story typeset on a sage background — one tall portrait image (1080 × 1350, Instagram-friendly) you can post or pin in a single tap."
          onDownload={() => downloadPosterAtSize(storyPosterRef.current, "letgo-story-poster-first-session.png", 1080, 1920)}
          onPrint={() => doPrint("print-story-poster")}
        >
          <div className="print-story-poster">
            <div
              ref={storyPosterRef}
              data-testid="story-poster-canvas"
              className="border border-[#E2DCD0] relative"
              style={{
                width: "4in",
                height: "7.1in",
                background: SAGE_GRADIENT,
                overflow: "hidden",
              }}
            >
              {/* Brushed grain */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
                  opacity: 0.5,
                }}
              />

              {/* Top brand row */}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3" style={{ paddingTop: "0.32in" }}>
                {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.34in", h: "0.34in" }} />}
                <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.09in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
                  Let Go Wellness
                </p>
              </div>

              {/* Title */}
              <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: "0.95in" }}>
                <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
                  A short story
                </p>
                <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.38in", lineHeight: 1.05, marginTop: "0.08in" }}>
                  The first session
                </p>
                <div style={{ width: "0.45in", height: "1px", background: "#E8C669", marginTop: "0.16in" }} />
              </div>

              {/* Body — full story typeset */}
              <div className="absolute left-0 right-0" style={{ top: "2.2in", bottom: "1.1in", padding: "0 0.5in", overflow: "hidden" }}>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, textAlign: "left" }}>
                  Marcus almost didn&apos;t come.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, marginTop: "0.1in" }}>
                  He sat in his car for fourteen minutes, thinking up reasons to drive away. But his daughter had asked him to try. Just once.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, marginTop: "0.1in" }}>
                  The therapist met him in the waiting room and said,{" "}
                  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                    &ldquo;You came. That&apos;s already a lot.&rdquo;
                  </span>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, marginTop: "0.1in" }}>
                  Inside, a linen sofa. Tea. A window with afternoon light. She didn&apos;t ask what was wrong — she asked,{" "}
                  <em style={{ color: "rgba(253,251,247,0.95)" }}>&ldquo;What&apos;s been the hardest week of the year for you?&rdquo;</em>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, marginTop: "0.1in" }}>
                  And for the first time in a long time, Marcus told the truth.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, marginTop: "0.1in" }}>
                  When the hour was up, she walked him to the door and said,{" "}
                  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                    &ldquo;You don&apos;t have to figure this out alone anymore.&rdquo;
                  </span>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.14in", lineHeight: 1.55, marginTop: "0.1in" }}>
                  Marcus sat in his car another fourteen minutes — this time, just breathing.
                </p>
              </div>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center" style={{ paddingBottom: "0.34in" }}>
                <div style={{ width: "0.45in", height: "1px", background: "rgba(232,198,105,0.5)", marginBottom: "0.14in" }} />
                <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.16in", lineHeight: 1.3, textAlign: "center" }}>
                  The hardest part is walking in.<br />We make the rest gentle.
                </p>
                <p className="uppercase mt-3" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.095in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>
                  letgowelllness.com
                </p>
              </div>
            </div>
          </div>
        </AssetSection>

        {/* — TEEN STRESS SERIES · 10 short stories — */}
        <section id="teen-stress-series" data-testid="teen-stress-series" className="border-t border-[#E2DCD0] pt-16 pb-6">
          <div className="mb-12">
            <p className="overline text-[#9F5C45] mb-3">Instagram · Teen Stress Series</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] tracking-tight">
              Ten stories about<br />what teens carry
            </h2>
            <p className="text-[#57534E] mt-3 max-w-2xl text-sm md:text-base">
              A 10-week posting series written specifically for parents, school counsellors, and teens themselves. Each poster covers a different facet of teen stress — perfectionism, social media, identity, college pressure, friendship loss, bullying, body image, sleep, family disconnect, and the locker she stopped opening. All 1080 × 1920, crisp.
            </p>
          </div>
        </section>

        {TEEN_STORIES.map((story) => (
          <AssetSection
            key={story.id}
            id={`teen-${story.id}`}
            eyebrow={`Teen Stress · ${story.tag}`}
            title={<>{story.titleStr} <span className="text-[#9F5C45] text-base align-middle">· single image</span></>}
            sub={`A short story for the ${story.tag.toLowerCase()} conversation. Tall portrait image (1080 × 1920).`}
            onDownload={() => downloadPosterAtSize(teenStressRefs.current[story.id], `letgo-teen-stress-${story.slug}.png`, 1080, 1920)}
          >
            <div className={`print-teen-${story.slug}`}>
              <StoryPosterCanvas
                ref={(el) => (teenStressRefs.current[story.id] = el)}
                brand={brand}
                logoUrl={logoUrl}
                eyebrow={story.eyebrow}
                title={story.title}
                footerLine={story.footerLine}
                footerCta={story.footerCta}
              >
                {story.body}
              </StoryPosterCanvas>
            </div>
          </AssetSection>
        ))}

        {/* — CAPTIONS PACK — downloadable .txt with all 10 captions — */}
        <AssetSection
          id="captions-pack"
          eyebrow="Instagram · Captions"
          title="Ready-to-paste captions · all 20 stories"
          sub="Every poster comes with a matching Instagram caption written in the Let Go voice — hook, body, soft CTA, and hashtags. 10 general + 10 teen-focused. Download once, copy/paste when you post."
          onDownload={() => {
            const all = [...STORY_CAPTIONS, ...TEEN_STORIES.map((t) => ({
              title: t.titleStr,
              tag: `Teen · ${t.tag}`,
              posterFile: `letgo-teen-stress-${t.slug}.png`,
              caption: t.caption,
            }))];
            const captions = all.map((c, i) =>
              `═══════════════════════════════════════════\n` +
              `STORY ${String(i + 1).padStart(2, "0")} · ${c.title}\n` +
              `Poster file: ${c.posterFile}\n` +
              `═══════════════════════════════════════════\n\n${c.caption}\n\n`
            ).join("");
            const header = `LET GO WELLNESS — INSTAGRAM CAPTIONS\nA quiet space to let go. · letgowelllness.com\nGenerated ${new Date().toLocaleDateString()}\n\nPost one per week. Use the matching poster (PNG) from /marketing.\n\n`;
            const blob = new Blob([header + captions], { type: "text/plain;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "letgo-instagram-captions.txt";
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
          }}
          downloadLabel="Download captions (.txt)"
        >
          <div className="space-y-4 max-w-3xl">
            {[...STORY_CAPTIONS, ...TEEN_STORIES.map((t) => ({
              title: t.titleStr,
              tag: `Teen · ${t.tag}`,
              posterFile: `letgo-teen-stress-${t.slug}.png`,
              caption: t.caption,
            }))].map((c, i) => (
              <details
                key={c.posterFile}
                data-testid={`caption-preview-${i + 1}`}
                className="border border-[#E2DCD0] bg-[#FDFBF7] rounded-sm"
              >
                <summary className="cursor-pointer px-5 py-4 flex items-center justify-between text-sm font-medium text-[#1C1917] hover:bg-[#F5F0E6] transition-colors">
                  <span className="flex items-center gap-3">
                    <span className="text-[#9F5C45] text-xs tracking-[0.22em] uppercase">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-serif text-base">{c.title}</span>
                  </span>
                  <span className="text-xs uppercase tracking-[0.22em] text-[#57534E]">{c.tag}</span>
                </summary>
                <div className="px-5 pb-5 pt-2 border-t border-[#E2DCD0]">
                  <pre className="whitespace-pre-wrap text-sm text-[#1C1917] leading-relaxed font-sans">{c.caption}</pre>
                  <button
                    type="button"
                    data-testid={`copy-caption-${i + 1}`}
                    onClick={() => {
                      navigator.clipboard.writeText(c.caption);
                      toast.success(`Caption ${i + 1} copied`);
                    }}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] btn-ghost"
                  >
                    Copy this caption
                  </button>
                </div>
              </details>
            ))}
          </div>
        </AssetSection>

        {/* — ANXIETY STORY POSTER — */}
        <AssetSection
          id="story-poster-anxiety"
          eyebrow="Instagram · Anxiety"
          title="The list at 2 a.m. · single image"
          sub="A short story about high-functioning anxiety and the cost of always being prepared. Tall portrait image (1080 × 1920)."
          onDownload={() => downloadPosterAtSize(anxietyPosterRef.current, "letgo-story-poster-the-list-at-2am.png", 1080, 1920)}
        >
          <div className="print-anxiety-poster">
            <StoryPosterCanvas
              ref={anxietyPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · Anxiety"
              title="The list at 2 a.m."
              footerLine={<>You can let your mind rest.<br />It has been working overtime.</>}
              footerCta="Anxiety therapy · letgowelllness.com"
            >
              <StoryP>She kept a list under her pillow.</StoryP>
              <StoryP mt="0.08in">Things to worry about. Things to fix. Things she had probably already messed up.</StoryP>
              <StoryP mt="0.08in">
                At 2 a.m., she added a new line. <Em>Did I sound weird in that email to my boss?</Em>
              </StoryP>
              <StoryP mt="0.08in">
                In the therapist&apos;s office she said, <Em>&ldquo;I think there&apos;s something wrong with my brain.&rdquo;</Em>
              </StoryP>
              <StoryP mt="0.08in">
                The therapist asked, <Gold>&ldquo;What does the list do for you?&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                It kept her safe, she said. If she worried about all of it, maybe none of it could surprise her.
              </StoryP>
              <StoryP mt="0.08in">
                <Gold>&ldquo;And what has the list taken from you?&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">Sleep. Friendships. Tuesdays.</StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — TEENS STORY POSTER — */}
        <AssetSection
          id="story-poster-teens"
          eyebrow="Instagram · Teens"
          title="She wouldn't look up · single image"
          sub="A short story about meeting teens exactly where they are. Tall portrait image (1080 × 1920)."
          onDownload={() => downloadPosterAtSize(teensPosterRef.current, "letgo-story-poster-she-wouldnt-look-up.png", 1080, 1920)}
        >
          <div className="print-teens-poster">
            <StoryPosterCanvas
              ref={teensPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · Teens"
              title={<>She wouldn&apos;t look up</>}
              footerLine={<>We meet teens where they are.<br />Even if that&apos;s the corner of the couch.</>}
              footerCta="Teen therapy · letgowelllness.com"
            >
              <StoryP>She wouldn&apos;t look up.</StoryP>
              <StoryP mt="0.08in">
                Hood pulled over her hair. Phone in her lap. One earbud in, one out — just in case.
              </StoryP>
              <StoryP mt="0.08in">
                Her mom had brought her. <Em>Forced</Em> her, the way she&apos;d put it on the drive over.
              </StoryP>
              <StoryP mt="0.08in">
                The therapist didn&apos;t ask her to take the hood off. She said,{" "}
                <Gold>&ldquo;You don&apos;t have to talk today. You just have to be here. That counts.&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                The therapist asked what music she was listening to. They listened to one song together. The whole song.
              </StoryP>
              <StoryP mt="0.08in">
                When it ended, she said, very quietly, <Em>&ldquo;Nobody asks me what I listen to anymore.&rdquo;</Em>
              </StoryP>
              <StoryP mt="0.08in">
                The hood stayed up. But she looked up. That was week one.
              </StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — RECOVERY / ADDICTION STORY POSTER — */}
        <AssetSection
          id="story-poster-recovery"
          eyebrow="Instagram · Recovery"
          title="The fourth time he came back · single image"
          sub="A short story about addiction, relapse, and the quiet courage of trying again. Tall portrait image (1080 × 1920)."
          onDownload={() => downloadPosterAtSize(recoveryPosterRef.current, "letgo-story-poster-the-fourth-time.png", 1080, 1920)}
        >
          <div className="print-recovery-poster">
            <StoryPosterCanvas
              ref={recoveryPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · Recovery"
              title="The fourth time he came back"
              footerLine={<>Recovery isn&apos;t a finish line.<br />It&apos;s a Tuesday. And then another.</>}
              footerCta="Addiction & recovery · letgowelllness.com"
            >
              <StoryP>The first time, he was angry.</StoryP>
              <StoryP mt="0.06in">The second time, he was ashamed.</StoryP>
              <StoryP mt="0.06in">The third time, he was silent.</StoryP>
              <StoryP mt="0.08in">
                The fourth time, he sat down and said, <Em>&ldquo;I&apos;m tired of starting over.&rdquo;</Em>
              </StoryP>
              <StoryP mt="0.08in">
                The therapist didn&apos;t ask why he had relapsed. She asked,{" "}
                <Gold>&ldquo;What was the longest you went?&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in"><Em>&ldquo;Eleven months.&rdquo;</Em></StoryP>
              <StoryP mt="0.08in">
                <Gold>&ldquo;Then you know how to do eleven months.&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                He cried. He hadn&apos;t cried in either of his rehab stays. He hadn&apos;t cried at his father&apos;s funeral.
              </StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — POSTPARTUM STORY POSTER — */}
        <AssetSection
          id="story-poster-postpartum"
          eyebrow="Instagram · Postpartum"
          title="After the baby came home · single image"
          sub="A short story about new motherhood — the kind no one warns you about. Tall portrait image (1080 × 1920) rendered at full Instagram resolution."
          onDownload={() => downloadPosterAtSize(postpartumPosterRef.current, "letgo-story-poster-after-the-baby-came-home.png", 1080, 1920)}
        >
          <div className="print-postpartum-poster">
            <StoryPosterCanvas
              ref={postpartumPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · Postpartum"
              title="After the baby came home"
              footerLine={<>You are not failing.<br />You are this depleted.</>}
              footerCta="Maternal mental health · letgowelllness.com"
            >
              <StoryP>She thought she would be glowing.</StoryP>
              <StoryP mt="0.08in">
                Instead, she stood at the kitchen sink at 3 a.m. holding a bottle that wasn&apos;t warming fast enough — wondering when her body had stopped feeling like her own.
              </StoryP>
              <StoryP mt="0.08in">
                She told the therapist she felt like a stranger. To her baby. To her husband. To the woman in the bathroom mirror.
              </StoryP>
              <StoryP mt="0.08in">
                The therapist didn&apos;t tell her this was normal. She said,{" "}
                <Gold>&ldquo;It is exhausting to love something this much while you are this depleted.&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">She nodded so hard she cried.</StoryP>
              <StoryP mt="0.08in">
                They talked about sleep. About help. About letting her mother do the laundry without apologizing.
              </StoryP>
              <StoryP mt="0.08in">
                By session four, she said something that surprised her: <Em>&ldquo;I had a really good Tuesday.&rdquo;</Em>
              </StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — BURNOUT STORY POSTER — */}
        <AssetSection
          id="story-poster-burnout"
          eyebrow="Instagram · Burnout"
          title="The exit she didn't take · single image"
          sub="A short story about quiet burnout and the moment you finally hear it. Tall portrait image (1080 × 1920)."
          onDownload={() => downloadPosterAtSize(burnoutPosterRef.current, "letgo-story-poster-the-exit-she-didnt-take.png", 1080, 1920)}
        >
          <div className="print-burnout-poster">
            <StoryPosterCanvas
              ref={burnoutPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · Burnout"
              title={<>The exit she didn&apos;t take</>}
              footerLine={<>It wasn&apos;t a vacation.<br />But it was a beginning.</>}
              footerCta="Burnout & anxiety · letgowelllness.com"
            >
              <StoryP>
                She was driving home from work, exactly the way she always did, when she missed her exit.
              </StoryP>
              <StoryP mt="0.08in">She didn&apos;t realize for nine miles.</StoryP>
              <StoryP mt="0.08in">
                When she finally pulled over, she put the car in park, turned off the radio, and sat. The dashboard glowed quietly. Her phone, for once, did not.
              </StoryP>
              <StoryP mt="0.08in">
                She thought, <Em>I cannot do another Monday.</Em>
              </StoryP>
              <StoryP mt="0.08in">
                A week later, she said it out loud:{" "}
                <Gold>&ldquo;I don&apos;t want to die. I want to disappear for a while. There&apos;s a difference.&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                The therapist nodded slowly. <Em>&ldquo;Yes. There is.&rdquo;</Em>
              </StoryP>
              <StoryP mt="0.08in">
                They made a list. Not a five-year plan — <Em>a Thursday.</Em>
              </StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — GRIEF STORY POSTER — */}
        <AssetSection
          id="story-poster-grief"
          eyebrow="Instagram · Grief"
          title="The empty chair · single image"
          sub="A short story about grief and the gentle permission to take your time. Tall portrait image (1080 × 1920)."
          onDownload={() => downloadPosterAtSize(griefPosterRef.current, "letgo-story-poster-the-empty-chair.png", 1080, 1920)}
        >
          <div className="print-grief-poster">
            <StoryPosterCanvas
              ref={griefPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · Grief"
              title="The empty chair"
              footerLine={<>You don&apos;t have to be ready.<br />You only have to be honest.</>}
              footerCta="Grief counselling · letgowelllness.com"
            >
              <StoryP>She still set two plates.</StoryP>
              <StoryP mt="0.08in">
                It had been eleven months. She knew. She just couldn&apos;t stop her hands.
              </StoryP>
              <StoryP mt="0.08in">
                The therapist didn&apos;t tell her to stop. She asked,{" "}
                <Gold>&ldquo;What does the second plate do for you?&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                She thought for a long time. <Em>&ldquo;It tells me he was real.&rdquo;</Em>
              </StoryP>
              <StoryP mt="0.08in">
                The therapist nodded. <Gold>&ldquo;Then keep setting it. For as long as you need to.&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                For months, everyone had been gently asking when she would &ldquo;move on.&rdquo; This was the first person who said: <Em>not yet, and that&apos;s okay.</Em>
              </StoryP>
              <StoryP mt="0.08in">
                On Tuesday last week, she set only one plate. And the world did not end.
              </StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — SPANISH STORY POSTER — */}
        <AssetSection
          id="story-poster-spanish"
          eyebrow="Instagram · En español"
          title="La primera cita · single image"
          sub="Una historia corta — para quien todavía está sentado en el estacionamiento. Imagen vertical (1080 × 1920) lista para Instagram."
          onDownload={() => downloadPosterAtSize(spanishPosterRef.current, "letgo-story-poster-la-primera-cita.png", 1080, 1920)}
        >
          <div className="print-spanish-poster">
            <StoryPosterCanvas
              ref={spanishPosterRef}
              brand={brand}
              logoUrl={logoUrl}
              eyebrow="Let Go Wellness · En español"
              title="La primera cita"
              footerLine={<>No tienes que hacer esto sola.<br />Estamos aquí.</>}
              footerCta="Terapia en español · letgowelllness.com"
            >
              <StoryP>Marcos casi no vino.</StoryP>
              <StoryP mt="0.08in">
                Se quedó sentado en su auto catorce minutos, con la mano en las llaves, pensando razones para irse. <Em>Estoy bien. La terapia es para otros.</Em>
              </StoryP>
              <StoryP mt="0.08in">
                Pero su hija le había pedido que lo intentara. Solo una vez.
              </StoryP>
              <StoryP mt="0.08in">
                La terapeuta lo recibió y le dijo,{" "}
                <Gold>&ldquo;Viniste. Eso ya es mucho.&rdquo;</Gold>
              </StoryP>
              <StoryP mt="0.08in">
                Adentro: un sofá de lino. Té. Una ventana con la luz de la tarde.
              </StoryP>
              <StoryP mt="0.08in">
                Ella no preguntó qué estaba mal. Preguntó,{" "}
                <Em>&ldquo;¿Cuál ha sido la semana más difícil del año para ti?&rdquo;</Em>
              </StoryP>
              <StoryP mt="0.08in">
                Por primera vez en mucho tiempo, Marcos dijo la verdad.
              </StoryP>
              <StoryP mt="0.08in">
                Cuando terminó la hora, ella le dijo:{" "}
                <Gold>&ldquo;Ya no tienes que enfrentar esto solo.&rdquo;</Gold>
              </StoryP>
            </StoryPosterCanvas>
          </div>
        </AssetSection>

        {/* — TRAUMA / EMDR STORY POSTER — */}
        <AssetSection
          id="story-poster-trauma"
          eyebrow="Instagram · Trauma & EMDR"
          title="What the body knew · single image"
          sub="A short story about EMDR therapy and the way the body holds what we cannot yet say. Tall portrait image (1080 × 1920) rendered at full Instagram resolution — no more blur."
          onDownload={() => downloadPosterAtSize(traumaPosterRef.current, "letgo-story-poster-what-the-body-knew.png", 1080, 1920)}
          onPrint={() => doPrint("print-trauma-poster")}
        >
          <div className="print-trauma-poster">
            <div
              ref={traumaPosterRef}
              data-testid="trauma-poster-canvas"
              className="border border-[#E2DCD0] relative"
              style={{
                width: "4in",
                height: "7.1in",
                background: SAGE_GRADIENT,
                overflow: "hidden",
              }}
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
                  opacity: 0.5,
                }}
              />

              {/* Top brand */}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3" style={{ paddingTop: "0.32in" }}>
                {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.34in", h: "0.34in" }} />}
                <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.09in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
                  Let Go Wellness · Trauma
                </p>
              </div>

              {/* Title */}
              <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: "0.95in" }}>
                <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
                  A short story
                </p>
                <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.38in", lineHeight: 1.05, marginTop: "0.08in" }}>
                  What the body knew
                </p>
                <div style={{ width: "0.45in", height: "1px", background: "#E8C669", marginTop: "0.16in" }} />
              </div>

              {/* Body */}
              <div className="absolute left-0 right-0" style={{ top: "2in", bottom: "1in", padding: "0 0.5in", overflow: "hidden" }}>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5 }}>
                  She came in saying she didn&apos;t know why she was there.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  <em style={{ color: "rgba(253,251,247,0.95)" }}>&ldquo;I&apos;m fine. Really. I just — I jumped at the microwave timer this morning and burst into tears.&rdquo;</em>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  The therapist nodded.{" "}
                  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                    &ldquo;How long has your body been doing that?&rdquo;
                  </span>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  They didn&apos;t talk about the accident that day. They talked about her breathing. About where she felt safe in her body — and where she didn&apos;t.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  Week six, they tried EMDR. She traced the therapist&apos;s fingers back and forth with her eyes while remembering the night the headlights filled the windshield.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  She cried. She shook. Her hands went cold. And then — slowly — quiet.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  The therapist said,{" "}
                  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                    &ldquo;Your body just told us something it has been carrying for three years.&rdquo;
                  </span>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  She nodded. She didn&apos;t have to explain.
                </p>
              </div>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center" style={{ paddingBottom: "0.34in" }}>
                <div style={{ width: "0.45in", height: "1px", background: "rgba(232,198,105,0.5)", marginBottom: "0.14in" }} />
                <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.16in", lineHeight: 1.3, textAlign: "center" }}>
                  The body already knew.
                </p>
                <p className="uppercase mt-3" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.095in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>
                  Trauma therapy · letgowelllness.com
                </p>
              </div>
            </div>
          </div>
        </AssetSection>

        {/* — COUPLES STORY POSTER — */}
        <AssetSection
          id="story-poster-couples"
          eyebrow="Instagram · Couples Story"
          title="The pause between · single image"
          sub="A short couples-therapy story typeset on the sage background. One tall portrait image (1080 × 1920) ready to post."
          onDownload={() => downloadPosterAtSize(couplesPosterRef.current, "letgo-story-poster-the-pause-between.png", 1080, 1920)}
          onPrint={() => doPrint("print-couples-poster")}
        >
          <div className="print-couples-poster">
            <div
              ref={couplesPosterRef}
              data-testid="couples-poster-canvas"
              className="border border-[#E2DCD0] relative"
              style={{
                width: "4in",
                height: "7.1in",
                background: SAGE_GRADIENT,
                overflow: "hidden",
              }}
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
                  opacity: 0.5,
                }}
              />

              {/* Top brand */}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3" style={{ paddingTop: "0.32in" }}>
                {logoUrl && <SilverLogo src={logoUrl} alt={brand} variant="gold" size={{ w: "0.34in", h: "0.34in" }} />}
                <p className="uppercase" style={{ color: "rgba(253,251,247,0.7)", fontSize: "0.09in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
                  Let Go Wellness · Couples
                </p>
              </div>

              {/* Title */}
              <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: "0.95in" }}>
                <p className="uppercase" style={{ color: "rgba(253,251,247,0.55)", fontSize: "0.08in", letterSpacing: "0.32em", fontFamily: "Manrope, sans-serif" }}>
                  A short story
                </p>
                <p className="font-serif italic" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.38in", lineHeight: 1.05, marginTop: "0.08in" }}>
                  The pause between
                </p>
                <div style={{ width: "0.45in", height: "1px", background: "#E8C669", marginTop: "0.16in" }} />
              </div>

              {/* Body */}
              <div className="absolute left-0 right-0" style={{ top: "2in", bottom: "1in", padding: "0 0.5in", overflow: "hidden" }}>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5 }}>
                  They sat on opposite ends of the linen sofa.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  Maya was already mid-sentence when the therapist raised a hand and said,{" "}
                  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                    &ldquo;Before we go any further — can we just take a breath?&rdquo;
                  </span>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  Daniel exhaled like he&apos;d been holding it for years.
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  For seven months, every conversation had ended the same way. They didn&apos;t fight loud anymore. They fought <em style={{ color: "rgba(253,251,247,0.95)" }}>tired.</em>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  The therapist didn&apos;t ask who was right. She asked,{" "}
                  <em style={{ color: "rgba(253,251,247,0.95)" }}>&ldquo;When did you stop reaching for each other?&rdquo;</em>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  Maya&apos;s eyes filled. <em style={{ color: "rgba(253,251,247,0.95)" }}>&ldquo;After the baby.&rdquo;</em>{" "}
                  Daniel&apos;s voice cracked. <em style={{ color: "rgba(253,251,247,0.95)" }}>&ldquo;After I lost my mom.&rdquo;</em>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  <span style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontStyle: "italic", fontWeight: 400 }}>
                    &ldquo;You&apos;re not bad partners. You&apos;re two people grieving in opposite directions.&rdquo;
                  </span>
                </p>
                <p className="font-serif" style={{ color: IVORY, fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: "0.13in", lineHeight: 1.5, marginTop: "0.08in" }}>
                  By the end of the hour, Daniel had moved one cushion closer. Maya reached, finally, for his hand.
                </p>
              </div>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center" style={{ paddingBottom: "0.34in" }}>
                <div style={{ width: "0.45in", height: "1px", background: "rgba(232,198,105,0.5)", marginBottom: "0.14in" }} />
                <p className="font-serif italic" style={{ color: "rgba(253,251,247,0.92)", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "0.16in", lineHeight: 1.3, textAlign: "center" }}>
                  It wasn&apos;t a fix.<br />It was a beginning.
                </p>
                <p className="uppercase mt-3" style={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", fontSize: "0.095in", letterSpacing: "0.28em", fontFamily: "Manrope, sans-serif" }}>
                  Couples therapy · letgowelllness.com
                </p>
              </div>
            </div>
          </div>
        </AssetSection>

        {/* — INSTAGRAM CAROUSEL · "The first session" story · 8 slides — */}
        <AssetSection
          id="story-carousel"
          eyebrow="Instagram · Story Carousel"
          title={"The first session · 8-slide carousel"}
          sub="A short story about Marcus, a man who almost didn't come, and the therapist who met him at the door. Post all 8 slides as a single carousel — Instagram lets visitors swipe through. Each slide is a 1080 × 1080 PNG."
          onDownload={async () => {
            setBusy("zip");
            try {
              const zip = new JSZip();
              for (const slide of STORY_SLIDES) {
                const node = carouselSlideRefs.current[slide.id];
                if (!node) continue;
                const dataUrl = await nodeToHdDataUrl(node, 1080, 1080, IMPRINT_SAGE);
                const base64 = dataUrl.split(",")[1];
                zip.file(`letgo-carousel-first-session-${slide.id}.png`, base64, { base64: true });
              }
              const blob = await zip.generateAsync({ type: "blob" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "letgo-instagram-carousel-first-session.zip";
              a.click();
              setTimeout(() => URL.revokeObjectURL(a.href), 2000);
              toast.success("8 carousel slides downloaded");
            } catch (e) {
              toast.error("Couldn't bundle carousel slides");
            } finally {
              setBusy(null);
            }
          }}
          downloadLabel={busy === "zip" ? "Bundling…" : "Download all 8 (ZIP)"}
        >
          <div className="print-story-carousel space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {STORY_SLIDES.map((slide, i) => (
                <div key={slide.id} className="relative group">
                  <CarouselSlide
                    ref={(el) => (carouselSlideRefs.current[slide.id] = el)}
                    slide={slide}
                    brand={brand}
                    logoUrl={logoUrl}
                  />
                  <button
                    type="button"
                    onClick={() => downloadAtSize(carouselSlideRefs.current[slide.id], `letgo-carousel-first-session-${slide.id}.png`, 1080, 1080, IMPRINT_SAGE)}
                    data-testid={`download-carousel-${slide.id}`}
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                  >
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FDFBF7] text-[#1C1917] text-[10px] uppercase tracking-[0.28em]">
                      <Download className="w-3 h-3" /> Slide {String(i + 1).padStart(2, "0")}
                    </span>
                  </button>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#57534E] max-w-2xl leading-relaxed">
              <strong className="text-[#1C1917] font-medium">How to post:</strong> On Instagram, tap + → Post → select all 8 PNGs in order (01 → 08) → tap &ldquo;Share&rdquo;. They become a single swipeable carousel. Suggested caption: <em>&ldquo;A short story — for anyone who&rsquo;s still sitting in the parking lot.&rdquo;</em>
            </p>
          </div>
        </AssetSection>

        {/* — INSTAGRAM FEED PLANNER · launch grid · 9 tiles — */}
        <AssetSection
          id="feed-planner"
          eyebrow="Instagram · Launch Feed"
          title="Your first 9 posts · feed planner"
          sub="A unified 3 × 3 grid that makes your profile look established from day one. Post in this exact order, top-row first, working down. Each tile downloads as a 1080 × 1080 PNG."
          onDownload={async () => {
            setBusy("zip");
            try {
              const zip = new JSZip();
              for (const tile of FEED_TILES) {
                const node = feedTileRefs.current[tile.id];
                if (!node) continue;
                const dataUrl = await nodeToHdDataUrl(node, 1080, 1080, IMPRINT_SAGE);
                const base64 = dataUrl.split(",")[1];
                zip.file(`letgo-feed-${tile.id}.png`, base64, { base64: true });
              }
              const blob = await zip.generateAsync({ type: "blob" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "letgo-instagram-feed-9-tiles.zip";
              a.click();
              setTimeout(() => URL.revokeObjectURL(a.href), 2000);
              toast.success("9 feed tiles downloaded");
            } catch (e) {
              toast.error("Couldn't bundle feed tiles");
            } finally {
              setBusy(null);
            }
          }}
          downloadLabel={busy === "zip" ? "Bundling…" : "Download all 9 (ZIP)"}
        >
          <div className="print-feed-planner space-y-6">
            {/* The 3×3 grid — exactly how Instagram displays it */}
            <div className="inline-block border border-[#E2DCD0]" style={{ background: IMPRINT_DEEP, padding: "2px" }}>
              <div className="grid grid-cols-3" style={{ gap: "2px" }}>
                {FEED_TILES.map((tile, i) => (
                  <div key={tile.id} className="relative group">
                    <FeedTile
                      ref={(el) => (feedTileRefs.current[tile.id] = el)}
                      tile={tile}
                      brand={brand}
                      logoUrl={logoUrl}
                    />
                    {/* Per-tile download overlay (visible on hover) */}
                    <button
                      type="button"
                    onClick={() => downloadAtSize(feedTileRefs.current[tile.id], `letgo-feed-${tile.id}.png`, 1080, 1080, IMPRINT_SAGE)}
                      data-testid={`download-feed-${tile.id}`}
                      className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                    >
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FDFBF7] text-[#1C1917] text-[10px] uppercase tracking-[0.28em]">
                        <Download className="w-3 h-3" /> Tile {String(i + 1).padStart(2, "0")}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-[#57534E] max-w-2xl leading-relaxed">
              <strong className="text-[#1C1917] font-medium">How to use:</strong> Post these to Instagram in the order shown — bottom-right first (Tile 09), then right-to-left, bottom-to-top — so they land on your profile reading top-to-bottom, left-to-right. Or hover any tile and click <em>Tile XX</em> to download a single PNG.
            </p>
          </div>
        </AssetSection>

        {/* — STORY POST · "Invitation" social card — */}
        <AssetSection
          id="story-post"
          eyebrow="Instagram · Story Post"
          title="A linen sofa. A window with afternoon light."
          sub="Square 1080 × 1080 caption card. Drop straight into Instagram, Facebook, or a story. Pairs beautifully with a quiet photo of the clinic."
          onDownload={() => downloadAtSize(storyPostRef.current, "letgo-story-post-invitation.png", 1080, 1080, "#243B2C")}
          onPrint={() => doPrint("print-story-post")}
        >
          <div className="print-story-post">
            <div
              ref={storyPostRef}
              data-testid="story-post-canvas"
              className="border border-[#E2DCD0] relative"
              style={{
                width: "5.4in",
                height: "5.4in",
                background: `linear-gradient(135deg, ${IMPRINT_SAGE} 0%, ${IMPRINT_DEEP} 55%, ${IMPRINT_SAGE} 100%)`,
                overflow: "hidden",
              }}
            >
              {/* Brushed grain */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
                  opacity: 0.5,
                }}
              />

              {/* Soft gold corner ornaments */}
              <div
                className="absolute"
                style={{
                  top: "0.3in",
                  left: "0.3in",
                  width: "0.6in",
                  height: "1px",
                  background: "linear-gradient(90deg, transparent, #E8C669, transparent)",
                }}
              />
              <div
                className="absolute"
                style={{
                  bottom: "0.3in",
                  right: "0.3in",
                  width: "0.6in",
                  height: "1px",
                  background: "linear-gradient(90deg, transparent, #E8C669, transparent)",
                }}
              />

              {/* Brand mark top */}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-center" style={{ paddingTop: "0.45in" }}>
                <p
                  className="uppercase"
                  style={{
                    color: "rgba(253,251,247,0.6)",
                    fontSize: "0.1in",
                    letterSpacing: "0.42em",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  Let Go Wellness
                </p>
              </div>

              {/* Story content — centered */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-12">
                <p
                  className="font-serif italic"
                  style={{
                    color: "#FDFBF7",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "0.42in",
                    lineHeight: 1.25,
                    letterSpacing: "0.005em",
                    maxWidth: "4.5in",
                  }}
                >
                  A linen sofa.<br />
                  A window with<br />
                  afternoon light.
                </p>

                <div
                  style={{
                    width: "0.5in",
                    height: "1px",
                    background: "#E8C669",
                    margin: "0.32in 0",
                  }}
                />

                <p
                  className="font-serif"
                  style={{
                    color: "#FDFBF7",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontWeight: 400,
                    fontSize: "0.22in",
                    lineHeight: 1.5,
                    maxWidth: "4in",
                  }}
                >
                  And someone who<br />finally listens.
                </p>

                <p
                  className="mt-6"
                  style={{
                    fontFamily: "'Sacramento', cursive",
                    fontSize: "0.36in",
                    letterSpacing: "0.04em",
                    lineHeight: 1,
                    background: "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  That&apos;s where healing starts.
                </p>
              </div>

              {/* CTA at bottom */}
              <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center" style={{ paddingBottom: "0.4in", gap: "0.06in" }}>
                <p
                  className="uppercase"
                  style={{
                    color: "rgba(253,251,247,0.85)",
                    fontSize: "0.1in",
                    letterSpacing: "0.32em",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  ↓ Book your first session
                </p>
                <p
                  className="uppercase"
                  style={{
                    color: "rgba(253,251,247,0.55)",
                    fontSize: "0.085in",
                    letterSpacing: "0.28em",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  letgowelllness.com
                </p>
              </div>
            </div>
          </div>
        </AssetSection>

        {/* — INSTAGRAM PROFILE LOGO (circle-safe) — */}
        <AssetSection
          id="instagram-profile"
          eyebrow="Instagram · Profile Picture"
          title="Profile logo · circle-safe 1080 × 1080"
          sub="Designed so every part of the wordmark stays comfortably inside the circular crop Instagram applies. Also works as a Facebook / LinkedIn / WhatsApp avatar."
          onDownload={() => downloadAtSize(instagramRef.current, "letgo-instagram-profile.png", 1080, 1080, "#243B2C")}
          onPrint={() => doPrint("print-instagram")}
        >
          <div className="print-instagram flex flex-wrap items-start gap-10">
            {/* Square canvas — Instagram crops to a circle on display. Wordmark is sized to fit inside the inner circle. */}
            <div
              ref={instagramRef}
              data-testid="instagram-profile-canvas"
              className="border border-[#E2DCD0] relative"
              style={{
                width: "3.5in",
                height: "3.5in",
                background: `linear-gradient(135deg, ${IMPRINT_SAGE} 0%, ${IMPRINT_DEEP} 50%, ${IMPRINT_SAGE} 100%)`,
                overflow: "hidden",
              }}
            >
              {/* Brushed grain */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 3px)",
                  opacity: 0.5,
                }}
              />

              {/* Center stack — logo + wordmark + script. Total stack is sized to fit inside a 3in safe circle. */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6">
                {logoUrl && (
                  <SilverLogo
                    src={logoUrl}
                    alt={brand}
                    variant="gold"
                    size={{ w: "1.25in", h: "1.25in" }}
                  />
                )}
                <p
                  className="font-serif uppercase"
                  style={{
                    color: "#FDFBF7",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontWeight: 600,
                    fontSize: "0.42in",
                    letterSpacing: "0.18em",
                    lineHeight: 1,
                    margin: 0,
                    textShadow: "0 1px 0 rgba(0,0,0,0.25)",
                  }}
                >
                  Let Go
                </p>
                <p
                  style={{
                    fontFamily: "'Sacramento', cursive",
                    fontSize: "0.34in",
                    letterSpacing: "0.06em",
                    lineHeight: 1,
                    margin: 0,
                    background: "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    filter: "drop-shadow(0 0.5px 0 rgba(0,0,0,0.4))",
                  }}
                >
                  Wellness
                </p>
              </div>
            </div>

            {/* Preview circle — shows how Instagram will crop it. Not part of the exported PNG. */}
            <div className="flex flex-col items-center gap-3">
              <p className="overline text-[#57534E]">Live preview · how Instagram displays it</p>
              <div
                className="rounded-full border-4 border-[#FDFBF7] shadow-xl overflow-hidden"
                style={{
                  width: "2in",
                  height: "2in",
                  background: `linear-gradient(135deg, ${IMPRINT_SAGE} 0%, ${IMPRINT_DEEP} 50%, ${IMPRINT_SAGE} 100%)`,
                }}
              >
                <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 px-3">
                  {logoUrl && (
                    <SilverLogo
                      src={logoUrl}
                      alt={brand}
                      variant="gold"
                      size={{ w: "0.7in", h: "0.7in" }}
                    />
                  )}
                  <p
                    className="font-serif uppercase"
                    style={{
                      color: "#FDFBF7",
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontWeight: 600,
                      fontSize: "0.24in",
                      letterSpacing: "0.16em",
                      lineHeight: 1,
                    }}
                  >
                    Let Go
                  </p>
                  <p
                    style={{
                      fontFamily: "'Sacramento', cursive",
                      fontSize: "0.2in",
                      letterSpacing: "0.04em",
                      lineHeight: 1,
                      background: "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Wellness
                  </p>
                </div>
              </div>
              <p className="text-xs text-[#57534E] max-w-[14rem] text-center leading-relaxed">
                Upload the square PNG to Instagram → the platform crops it into a circle automatically. All artwork stays safely inside the visible area.
              </p>
            </div>
          </div>
        </AssetSection>

        {/* — REFERRAL POSTCARD — */}
        <AssetSection
          id="postcard"
          eyebrow="Referral Postcard"
          title="Direct-mail postcard"
          sub="USPS standard 6″ × 4″. Hand to patients at checkout, or mail to lapsed clients. Front speaks to the heart; back has a real mailing address area + QR."
          onDownload={async () => {
            await downloadAsPDF(postcardFrontRef.current, "letgo-postcard-front.pdf", 6, 4, "#3A5A40");
            await downloadAsPDF(postcardBackRef.current, "letgo-postcard-back.pdf", 6, 4, "#3A5A40");
          }}
          downloadLabel="Download PDF (front + back)"
          onVectorPdf={async () => {
            const t = toast.loading("Drawing your true-vector postcard…");
            try {
              await buildPostcardVectorPdf({
                brand,
                phone,
                addr1,
                addr2,
                logoUrl,
                domain: DOMAIN,
                domainUrl: DOMAIN_URL,
                filename: "letgo-postcard-vector.pdf",
              });
              toast.success("True-vector postcard saved — front + back as pure vector.", { id: t });
            } catch (e) {
              console.error(e);
              toast.error("Could not build vector postcard. Try again.", { id: t });
            }
          }}
          onDownloadSvg={async () => {
            const t = toast.loading("Exporting SVG (front + back)…");
            try {
              await downloadAsSvg(postcardFrontRef.current, "letgo-postcard-front.svg");
              await downloadAsSvg(postcardBackRef.current, "letgo-postcard-back.svg");
              toast.success("Postcard SVGs saved.", { id: t });
            } catch (e) {
              console.error(e);
              toast.error("Could not build SVG. Try again.", { id: t });
            }
          }}
          onEmail={() =>
            emailToPrinter(postcardFrontRef.current, "letgo-postcard-front", 6, 4, {
              backgroundColor: "#3A5A40",
              subject: "Print order — Let Go Wellness postcard",
              notes: "USPS 6\" × 4\" postcard · 14pt or heavier cardstock · full-bleed front. I'll send back-side artwork separately. Quantity: __.",
            })
          }
          onPrint={() => doPrint("print-postcard")}
        >
          <div className="flex flex-wrap gap-8 print-postcard">
            <div className="space-y-2">
              <p className="overline text-[#57534E]">Front</p>
              <div ref={postcardFrontRef} data-testid="postcard-front" className="border border-[#E2DCD0]">
                <ThemedPostcardFront theme={THEMES.imprint} brand={brand} logoUrl={logoUrl} />
              </div>
            </div>
            <div className="space-y-2">
              <p className="overline text-[#57534E]">Back · address side</p>
              <div ref={postcardBackRef} data-testid="postcard-back" className="border border-[#E2DCD0]">
                <ThemedPostcardBack
                  theme={THEMES.imprint}
                  brand={brand}
                  phone={phone}
                  addr1={addr1}
                  addr2={addr2}
                />
              </div>
            </div>
          </div>
        </AssetSection>

        {/* — WINDOW DECAL — */}
        <AssetSection
          id="decal"
          eyebrow="Window / Door Decal"
          title="Clinic window decal · 6″ × 6″"
          sub="Send the PNG to any local sign shop (or order online from StickerYou / Vistaprint). Rounded square, sage on cream — meant to live on glass."
          onDownload={() => downloadAsPDF(decalRef.current, "letgo-window-decal.pdf", 4, 6, "#FDFBF7")}
          onDownloadSvg={() => downloadAsSvg(decalRef.current, "letgo-window-decal.svg")}
          onEmail={() => emailToPrinter(decalRef.current, "letgo-window-decal", 4, 6, { subject: "Print order — Let Go Wellness window decal", notes: "4\" × 6\" vinyl decal · static cling or transfer adhesive · color reference: deep sage #3A5A40 + metallic gold." })}
          downloadLabel="Download PDF"
          onPrint={() => doPrint("print-decal")}
        >
          <div className="print-decal">
            <div ref={decalRef} data-testid="decal-canvas">
              <ThemedWindowDecal theme={THEMES.imprint} brand={brand} logoUrl={logoUrl} />
            </div>
          </div>
        </AssetSection>

        {/* — INTAKE QR STICKER — 3" × 3" sage sticker linking to /intake — */}
        <AssetSection
          id="intake-qr"
          eyebrow="Intake QR Sticker"
          title="Scan-to-intake sticker · 3″ × 3″"
          sub="Print on square vinyl or clear label stock — clip to appointment cards, tape to the front-desk clipboard, or hand out at referrer offices. Scanning drops the patient straight into the two-step intake flow."
          onDownload={() => downloadAsPDF(intakeStickerRef.current, "letgo-intake-sticker.pdf", 3, 3, "#3A5A40")}
          onDownloadSvg={() => downloadAsSvg(intakeStickerRef.current, "letgo-intake-sticker.svg")}
          onEmail={() => emailToPrinter(intakeStickerRef.current, "letgo-intake-sticker", 3, 3, { backgroundColor: "#3A5A40", subject: "Print order — Let Go Wellness intake QR sticker", notes: "3\" × 3\" square vinyl or clear label · sage background #3A5A40 · gold + cream ink. Quantity: __." })}
          downloadLabel="Download PDF"
          onPrint={() => doPrint("print-intake-qr")}
        >
          <div className="print-intake-qr">
            <div
              ref={intakeStickerRef}
              data-testid="intake-sticker-canvas"
              style={{
                width: "3in",
                height: "3in",
                background: SAGE_GRADIENT,
                color: IVORY,
                padding: "0.25in",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                borderRadius: "0.18in",
                boxShadow: "0 20px 50px -20px rgba(36,59,44,0.5)",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <p style={{
                  fontFamily: "Inter, system-ui, sans-serif",
                  fontSize: "8px",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: "#E8C669",
                  margin: 0,
                }}>
                  Let Go Wellness
                </p>
                <p style={{
                  fontFamily: "Cormorant Garamond, Georgia, serif",
                  fontSize: "18px",
                  fontStyle: "italic",
                  color: IVORY,
                  margin: "2px 0 0",
                  lineHeight: 1,
                }}>
                  Scan to complete<br/>your intake.
                </p>
              </div>

              <div style={{ background: "#FDFBF7", padding: "6px", borderRadius: "4px" }}>
                <QRCode value={`${DOMAIN_URL}/intake`} size={140} bgColor="#FDFBF7" fgColor="#243B2C" />
              </div>

              <p style={{
                fontFamily: "Inter, system-ui, sans-serif",
                fontSize: "7px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#E8C669",
                margin: 0,
                textAlign: "center",
              }}>
                {DOMAIN} / intake
              </p>
            </div>
          </div>
        </AssetSection>

        {/* — EMAIL SIGNATURE — */}
        <section id="signature" data-testid="asset-signature" className="border-t border-[#E2DCD0] pt-16 pb-32">
          <div className="mb-10">
            <p className="overline text-[#9F5C45] mb-3">Email Signature</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1C1917] tracking-tight">
              Sign off, on brand.
            </h2>
            <p className="text-[#57534E] mt-3 max-w-xl text-sm md:text-base">
              Click <em>Copy signature</em>, then paste directly into Gmail, Outlook or Apple Mail&apos;s
              signature settings. Works in rich-text mode.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">
            {PEOPLE.map((person) => (
              <div key={person.key} className="bg-[#F4F1EA] p-8 border border-[#E2DCD0]" data-testid={`signature-${person.key}`}>
                <p className="overline text-[#9F5C45] mb-6">{person.name}</p>
                <div className="bg-white p-6 mb-6 overflow-x-auto">
                  <EmailSignaturePreview
                    person={person}
                    brand={brand}
                    logoUrl={logoUrl}
                    phone={phone}
                    email={email}
                    addr1={addr1}
                    addr2={addr2}
                  />
                </div>
                <button
                  onClick={() => copySig(person)}
                  data-testid={`copy-signature-${person.key}`}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 btn-primary text-xs tracking-[0.22em] uppercase"
                >
                  {copiedKey === person.key ? (
                    <>
                      <Check className="w-4 h-4" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> Copy signature
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="border-t border-[#E2DCD0] mt-20 pt-10">
            <h3 className="font-serif text-3xl text-[#1C1917] mb-4">Printing tips</h3>
            <ul className="text-[#57534E] space-y-2 list-disc list-inside text-sm md:text-base">
              <li>Use the <strong>Download PNG</strong> buttons for high-res files you can send to any local printer or Vistaprint.</li>
              <li>For business cards: print front and back on heavy stock (16pt+ matte recommended), then trim to 3.5″ × 2″.</li>
              <li>Use the <strong>Print PDF</strong> button to save as PDF from your browser&apos;s print dialog (set &ldquo;Background graphics&rdquo; ON, margins to None).</li>
              <li>All QR codes scan to <strong>letgowelllness.com</strong> — already tested.</li>
            </ul>
          </div>
        </section>
      </div>

      <Footer />

      {/* Print-only styles: when a print target is set, hide everything else */}
      <style>{`
        @media print {
          @page { margin: 0.25in; }
          body { background: #FFFFFF; }
          header, footer, nav, .print\\:hidden { display: none !important; }
        }
        ${printTarget ? `
          @media print {
            body * { visibility: hidden !important; }
            .${printTarget}, .${printTarget} * { visibility: visible !important; }
            .${printTarget} { position: absolute; left: 0; top: 0; }
          }
        ` : ""}
      `}</style>
    </main>
  );
}
